import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowRight, Building2, Package, FileText, Download, Weight, Home, Check, X, Save, Truck, Plus, Trash2, Edit2, Layers, Upload, RefreshCw, FileSpreadsheet, Calendar, AlertCircle, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/buildings`;

export default function ProjectDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [calculation, setCalculation] = useState(null);
  const [supplyStatus, setSupplyStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  
  // Template management
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    code: '', name: '', description: '', area: 0, rooms_count: 0, bathrooms_count: 0, count: 0, materials: []
  });
  
  // Material addition with search
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [materialQty, setMaterialQty] = useState(1);
  const [unitMaterialSearch, setUnitMaterialSearch] = useState('');
  const [areaMaterialSearch, setAreaMaterialSearch] = useState('');
  
  // Supply tracking
  const [editingSupply, setEditingSupply] = useState(null);
  const [receivedQty, setReceivedQty] = useState(0);
  const [supplySearch, setSupplySearch] = useState('');
  const [supplyFilter, setSupplyFilter] = useState('all'); // all, not_received, partial, complete
  const [supplyPage, setSupplyPage] = useState(1);
  const SUPPLY_PER_PAGE = 20;
  
  // Area materials (مواد المساحة)
  const [showAddAreaMaterial, setShowAddAreaMaterial] = useState(false);
  const [newAreaMaterial, setNewAreaMaterial] = useState({
    material_id: '', material_code: '', material_name: '', unit: '', factor: 0, unit_price: 0, notes: '', calculation_type: 'all_floors', selected_floors: [],
    tile_width: 0, tile_height: 0, waste_percentage: 0
  });
  const [editingAreaMaterial, setEditingAreaMaterial] = useState(null);

  // Building-wide materials (مواد العمارة الكلية)
  const [buildingMaterials, setBuildingMaterials] = useState([]);
  const [showAddBuildingMaterial, setShowAddBuildingMaterial] = useState(false);
  const [newBuildingMaterial, setNewBuildingMaterial] = useState({
    material_id: '', material_code: '', material_name: '', unit: '', quantity: 0, unit_price: 0, notes: ''
  });
  const [editingBuildingMaterial, setEditingBuildingMaterial] = useState(null);

  // Purchases (المشتريات الفعلية)
  const [purchases, setPurchases] = useState([]);
  const [showAddPurchase, setShowAddPurchase] = useState(false);
  const [newPurchase, setNewPurchase] = useState({
    material_code: '', quantity: 0, unit_price: 0, supplier: '', purchase_date: '', invoice_number: '', notes: ''
  });
  const [comparison, setComparison] = useState(null);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const purchaseImportRef = useRef(null);

  // الجدول الزمني
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState('all'); // all | upcoming | in_progress | completed | upcoming_soon
  const timelineFileRef = useRef(null);

  // مشتريات غير مخططة (الأصناف غير المربوطة من آخر ملف Excel مستورد)
  const [unmapped, setUnmapped] = useState(null);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [addingToPlanned, setAddingToPlanned] = useState(null); // item_number being added
  const [addUnmappedModal, setAddUnmappedModal] = useState(null); // {itemNumber, itemName, mode, ...}
  const [categories, setCategories] = useState([]); // material categories
  // فلاتر متقدمة للمشتريات غير المخططة
  const [unmappedFilters, setUnmappedFilters] = useState({
    q: '', supplier: '', unit: '', date_from: '', date_to: '', min_qty: '', min_cost: ''
  });
  const [unmappedFilterOptions, setUnmappedFilterOptions] = useState({ suppliers: [], units: [] });

  const projectImportRef = useRef(null);

  // Export project to Excel
  const handleExportProjectExcel = async () => {
    try {
      toast.info('جاري تصدير المشروع...');
      const response = await axios.get(`${API}/projects/${id}/export/project-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `مشروع_${project.name.replace(/\s+/g, '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('تم تصدير المشروع بنجاح');
    } catch (error) {
      toast.error('خطأ في تصدير المشروع');
      console.error(error);
    }
  };

  // Import project from Excel
  const handleImportProjectExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      toast.info('جاري استيراد البيانات...');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/projects/${id}/import/project-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message || 'تم استيراد البيانات بنجاح');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في استيراد الملف');
      console.error('Import error:', error);
    }
    
    if (projectImportRef.current) {
      projectImportRef.current.value = '';
    }
  };
  
  // Floors management (الأدوار)
  const [showAddFloor, setShowAddFloor] = useState(false);
  const [newFloor, setNewFloor] = useState({
    floor_number: 0, floor_name: '', area: 0, steel_factor: 120
  });

  // فلترة المواد حسب البحث
  const filteredUnitMaterials = materials
    .filter(m => m.material_type === 'unit' || m.material_type === 'shared')
    .filter(m => !unitMaterialSearch || 
      m.name.toLowerCase().includes(unitMaterialSearch.toLowerCase()) ||
      m.code.toLowerCase().includes(unitMaterialSearch.toLowerCase())
    );

  const filteredAreaMaterials = materials
    .filter(m => m.material_type === 'area' || m.material_type === 'shared')
    .filter(m => !areaMaterialSearch || 
      m.name.toLowerCase().includes(areaMaterialSearch.toLowerCase()) ||
      m.code.toLowerCase().includes(areaMaterialSearch.toLowerCase())
    );

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  // Auto-load unmapped imports when tab is activated
  useEffect(() => {
    if (activeTab === 'unplanned' && !unmapped && !unmappedLoading) {
      loadUnmapped();
    }
    if (activeTab === 'timeline' && !timeline && !timelineLoading) {
      loadTimeline();
    }
  }, [activeTab]);

  const loadTimeline = async () => {
    setTimelineLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${id}/timeline`);
      setTimeline(res.data);
    } catch {
      toast.error('فشل في تحميل الجدول الزمني');
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleImportTimeline = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      toast.info('جاري استيراد الجدول الزمني...');
      const res = await axios.post(`${API}/projects/${id}/timeline/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data?.message || 'تم الاستيراد بنجاح');
      if (Array.isArray(res.data?.warnings) && res.data.warnings.length > 0) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      await loadTimeline();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الاستيراد');
    } finally {
      if (timelineFileRef.current) timelineFileRef.current.value = '';
    }
  };

  const handleClearTimeline = async () => {
    if (!window.confirm('هل تريد حذف الجدول الزمني بالكامل؟')) return;
    try {
      await axios.delete(`${API}/projects/${id}/timeline`);
      toast.success('تم الحذف');
      setTimeline(null);
      await loadTimeline();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحذف');
    }
  };

  const fetchData = async () => {
    try {
      const [projectRes, materialsRes, calcRes, supplyRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/materials`),
        axios.get(`${API}/projects/${id}/calculate`).catch(() => ({ data: null })),
        axios.get(`${API}/projects/${id}/supply-status`).catch(() => ({ data: [] }))
      ]);
      setProject(projectRes.data);
      setMaterials(materialsRes.data);
      setCalculation(calcRes.data);
      setSupplyStatus(supplyRes.data);
      setBuildingMaterials(projectRes.data?.building_materials || []);
      setPurchases(projectRes.data?.building_purchases || []);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  // Template Management
  const handleAddTemplate = async () => {
    if (!newTemplate.code || !newTemplate.name) {
      toast.error('الرجاء إدخال الكود والاسم');
      return;
    }
    try {
      await axios.post(`${API}/projects/${id}/templates`, newTemplate);
      toast.success('تم إضافة النموذج');
      setShowAddTemplate(false);
      setNewTemplate({ code: '', name: '', description: '', area: 0, rooms_count: 0, bathrooms_count: 0, count: 0, materials: [] });
      fetchData();
    } catch (error) {
      toast.error('خطأ في الإضافة');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      await axios.put(`${API}/projects/${id}/templates/${selectedTemplate.id}`, selectedTemplate);
      toast.success('تم الحفظ');
      fetchData();
    } catch (error) {
      toast.error('خطأ في الحفظ');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/projects/${id}/templates/${templateId}`);
      toast.success('تم الحذف');
      if (selectedTemplate?.id === templateId) setSelectedTemplate(null);
      fetchData();
    } catch (error) {
      toast.error('خطأ في الحذف');
    }
  };

  const handleAddMaterialToTemplate = () => {
    if (!selectedMaterial || !selectedTemplate) return;
    const mat = materials.find(m => m.id === selectedMaterial);
    if (!mat) return;
    
    const newMat = {
      material_id: mat.id,
      material_code: mat.code,
      material_name: mat.name,
      unit: mat.unit,
      quantity_per_unit: materialQty,
      unit_price: mat.unit_price || 0
    };
    
    setSelectedTemplate(prev => ({
      ...prev,
      materials: [...(prev.materials || []), newMat]
    }));
    setSelectedMaterial('');
    setMaterialQty(1);
  };

  const handleRemoveMaterialFromTemplate = (idx) => {
    setSelectedTemplate(prev => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== idx)
    }));
  };

  // Area Materials Management (مواد المساحة)
  const handleAddAreaMaterial = async () => {
    if (!newAreaMaterial.material_id) {
      toast.error('الرجاء اختيار المادة');
      return;
    }
    // يجب إدخال المعامل أو مقاس البلاط
    if (!newAreaMaterial.factor && !newAreaMaterial.tile_width) {
      toast.error('الرجاء إدخال المعامل أو مقاس البلاط');
      return;
    }
    if (newAreaMaterial.calculation_type === 'selected_floors' && newAreaMaterial.selected_floors.length === 0) {
      toast.error('الرجاء اختيار دور واحد على الأقل');
      return;
    }
    try {
      await axios.post(`${API}/projects/${id}/area-materials`, newAreaMaterial);
      toast.success('تم إضافة المادة');
      setShowAddAreaMaterial(false);
      setNewAreaMaterial({
        material_id: '', material_code: '', material_name: '', unit: '', factor: 0, unit_price: 0, notes: '', calculation_type: 'all_floors', selected_floors: [],
        tile_width: 0, tile_height: 0, waste_percentage: 0
      });
      fetchData();
    } catch (error) {
      toast.error('خطأ في الإضافة');
    }
  };

  const handleDeleteAreaMaterial = async (materialId) => {
    try {
      await axios.delete(`${API}/projects/${id}/area-materials/${materialId}`);
      toast.success('تم الحذف');
      fetchData();
    } catch (error) {
      toast.error('خطأ في الحذف');
    }
  };

  const handleEditAreaMaterial = (mat) => {
    setEditingAreaMaterial({
      id: mat.id,
      material_id: mat.material_id,
      material_code: mat.material_code,
      material_name: mat.material_name,
      unit: mat.unit,
      factor: mat.factor,
      unit_price: mat.unit_price,
      notes: mat.notes || '',
      calculation_type: mat.calculation_type || 'all_floors',
      selected_floors: mat.selected_floors || [],
      tile_width: mat.tile_width || 0,
      tile_height: mat.tile_height || 0,
      waste_percentage: mat.waste_percentage || 0
    });
    setShowAddAreaMaterial(false);
  };

  const handleUpdateAreaMaterial = async () => {
    if (!editingAreaMaterial.factor && !editingAreaMaterial.tile_width) {
      toast.error('الرجاء إدخال المعامل أو مقاس البلاط');
      return;
    }
    if (editingAreaMaterial.calculation_type === 'selected_floors' && editingAreaMaterial.selected_floors.length === 0) {
      toast.error('الرجاء اختيار دور واحد على الأقل');
      return;
    }
    try {
      await axios.put(`${API}/projects/${id}/area-materials/${editingAreaMaterial.id}`, editingAreaMaterial);
      toast.success('تم التحديث');
      setEditingAreaMaterial(null);
      fetchData();
    } catch (error) {
      toast.error('خطأ في التحديث');
    }
  };

  const handleToggleFloorSelectionEdit = (floorNumber) => {
    setEditingAreaMaterial(prev => {
      const selected = prev.selected_floors || [];
      if (selected.includes(floorNumber)) {
        return { ...prev, selected_floors: selected.filter(f => f !== floorNumber) };
      } else {
        return { ...prev, selected_floors: [...selected, floorNumber] };
      }
    });
  };

  const handleToggleFloorSelection = (floorNumber) => {
    setNewAreaMaterial(prev => {
      const selected = prev.selected_floors || [];
      if (selected.includes(floorNumber)) {
        return { ...prev, selected_floors: selected.filter(f => f !== floorNumber) };
      } else {
        return { ...prev, selected_floors: [...selected, floorNumber] };
      }
    });
  };

  const handleSelectAreaMaterial = (matId) => {
    const mat = materials.find(m => m.id === matId);
    if (mat) {
      setNewAreaMaterial(prev => ({
        ...prev,
        material_id: mat.id,
        material_code: mat.code,
        material_name: mat.name,
        unit: mat.unit,
        unit_price: mat.unit_price || 0
      }));
    }
  };

  // Building-Wide Materials Management (مواد العمارة الكلية)
  const handleBuildingMaterialSelect = (matId) => {
    const mat = materials.find(m => m.id === matId);
    if (mat) {
      setNewBuildingMaterial(prev => ({
        ...prev,
        material_id: mat.id,
        material_code: mat.code,
        material_name: mat.name,
        unit: mat.unit,
        unit_price: prev.unit_price || mat.unit_price || 0
      }));
    }
  };

  const handleAddBuildingMaterial = async () => {
    if (!newBuildingMaterial.material_id || !newBuildingMaterial.quantity || newBuildingMaterial.quantity <= 0) {
      toast.error('الرجاء اختيار المادة وإدخال كمية صحيحة');
      return;
    }
    try {
      const res = await axios.post(`${API}/projects/${id}/building-materials`, newBuildingMaterial);
      setBuildingMaterials(prev => [...prev, res.data]);
      setShowAddBuildingMaterial(false);
      setNewBuildingMaterial({ material_id: '', material_code: '', material_name: '', unit: '', quantity: 0, unit_price: 0, notes: '' });
      toast.success('تمت إضافة المادة على مستوى العمارة');
    } catch {
      toast.error('فشل في الإضافة');
    }
  };

  const handleUpdateBuildingMaterial = async () => {
    if (!editingBuildingMaterial) return;
    try {
      await axios.put(`${API}/projects/${id}/building-materials/${editingBuildingMaterial.id}`, editingBuildingMaterial);
      setBuildingMaterials(prev => prev.map(m => m.id === editingBuildingMaterial.id ? editingBuildingMaterial : m));
      setEditingBuildingMaterial(null);
      toast.success('تم التحديث');
    } catch {
      toast.error('فشل في التحديث');
    }
  };

  const handleDeleteBuildingMaterial = async (mid) => {
    if (!window.confirm('حذف هذه المادة من قائمة مواد العمارة؟')) return;
    try {
      await axios.delete(`${API}/projects/${id}/building-materials/${mid}`);
      setBuildingMaterials(prev => prev.filter(m => m.id !== mid));
      toast.success('تم الحذف');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const parts = [detail.message || 'لا يمكن الحذف'];
        if (detail.purchases_count) parts.push(`مشتريات مربوطة: ${detail.purchases_count}`);
        if (detail.supply_count) parts.push(`سجلات توريد: ${detail.supply_count}`);
        toast.error(parts.join(' • '), { duration: 8000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'فشل في الحذف');
      }
    }
  };

  // === استيراد كميات مبدئية من Excel ===
  const bmImportInputRef = useRef(null);
  const [bmImporting, setBmImporting] = useState(false);
  const [bmImportResult, setBmImportResult] = useState(null);

  const handleImportBuildingMaterialsXlsx = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setBmImporting(true);
      const res = await axios.post(
        `${API}/projects/${id}/building-materials/import-xlsx`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const data = res.data || {};
      // إعادة تحميل قائمة المواد
      const projectRes = await axios.get(`${API}/projects/${id}`);
      setBuildingMaterials(projectRes.data?.building_materials || []);
      setBmImportResult(data);
      toast.success(data.message || 'تم الاستيراد بنجاح');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setBmImportResult({ error: true, ...detail });
        toast.error(detail.message || 'فشل الاستيراد');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'فشل في استيراد الملف');
      }
    } finally {
      setBmImporting(false);
      if (bmImportInputRef.current) bmImportInputRef.current.value = '';
    }
  };

  // === Purchases (المشتريات الفعلية) ===
  const handleAddPurchase = async () => {
    if (!newPurchase.material_code || !newPurchase.quantity) {
      toast.error('الكود والكمية مطلوبان');
      return;
    }
    try {
      const res = await axios.post(`${API}/projects/${id}/purchases`, newPurchase);
      setPurchases(p => [...p, res.data]);
      setShowAddPurchase(false);
      setNewPurchase({ material_code: '', quantity: 0, unit_price: 0, supplier: '', purchase_date: '', invoice_number: '', notes: '' });
      toast.success('تمت إضافة عملية الشراء');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل في الإضافة');
    }
  };

  const handleDeletePurchase = async (pid) => {
    if (!window.confirm('حذف عملية الشراء؟')) return;
    try {
      await axios.delete(`${API}/projects/${id}/purchases/${pid}`);
      setPurchases(p => p.filter(x => x.id !== pid));
      toast.success('تم الحذف');
    } catch {
      toast.error('فشل في الحذف');
    }
  };

  const [importResult, setImportResult] = useState(null); // نتيجة استيراد المشتريات الفعلية

  const handleImportPurchasesExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      toast.info('جاري الاستيراد... قد يستغرق بعض الوقت للملفات الكبيرة');
      const res = await axios.post(`${API}/projects/${id}/purchases/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = res.data || {};
      const { imported_count = 0, duplicates_skipped = 0, unlinked_count = 0, not_in_accounting_count = 0 } = data;
      // إعرض المودال بالتفاصيل
      setImportResult(data);
      if (imported_count > 0) {
        toast.success(`تم استيراد ${imported_count} عملية شراء بنجاح`);
      } else if (duplicates_skipped > 0 && unlinked_count === 0 && not_in_accounting_count === 0) {
        toast.info(`لا توجد عمليات جديدة — ${duplicates_skipped} صف موجود مسبقاً`);
      } else {
        toast.warning('لم يتم استيراد أي صف. راجع تفاصيل النتيجة');
      }
      // refetch
      const proj = await axios.get(`${API}/projects/${id}`);
      setPurchases(proj.data?.building_purchases || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل في الاستيراد');
    } finally {
      if (purchaseImportRef.current) purchaseImportRef.current.value = '';
    }
  };

  const loadComparison = async () => {
    try {
      const params = new URLSearchParams();
      if (filterSupplier) params.append('supplier', filterSupplier);
      if (filterCategory) params.append('category', filterCategory);
      const qs = params.toString() ? `?${params}` : '';
      const res = await axios.get(`${API}/projects/${id}/comparison-report${qs}`);
      setComparison(res.data);
    } catch {
      toast.error('فشل في تحميل تقرير المقارنة');
    }
  };

  const buildUnmappedQS = () => {
    const params = new URLSearchParams();
    Object.entries(unmappedFilters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) params.append(k, v);
    });
    return params.toString();
  };

  const loadUnmapped = async () => {
    setUnmappedLoading(true);
    try {
      const qs = buildUnmappedQS();
      const [dataRes, optsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}/unmapped-imports${qs ? `?${qs}` : ''}`),
        axios.get(`${API}/projects/${id}/unmapped-imports/filters`).catch(() => ({ data: { suppliers: [], units: [] } })),
      ]);
      setUnmapped(dataRes.data);
      setUnmappedFilterOptions(optsRes.data);
    } catch {
      toast.error('فشل في تحميل الأصناف غير المربوطة');
    } finally {
      setUnmappedLoading(false);
    }
  };

  const resetUnmappedFilters = () => {
    setUnmappedFilters({ q: '', supplier: '', unit: '', date_from: '', date_to: '', min_qty: '', min_cost: '' });
    setTimeout(() => loadUnmapped(), 0);
  };

  const handleExportUnmappedExcel = async () => {
    try {
      toast.info('جاري إنشاء ملف Excel...');
      const qs = buildUnmappedQS();
      const res = await axios.get(`${API}/projects/${id}/unmapped-imports/excel${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `unmapped_purchases_${project?.name || id}.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('تم تنزيل الملف');
    } catch {
      toast.error('فشل في التصدير');
    }
  };

  const handleExportUnmappedPdf = async () => {
    try {
      toast.info('جاري إنشاء ملف PDF...');
      const qs = buildUnmappedQS();
      const res = await axios.get(`${API}/projects/${id}/unmapped-imports/pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `unmapped_purchases_${project?.name || id}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('تم تنزيل الملف');
    } catch {
      toast.error('فشل في التصدير');
    }
  };

  const handleAddUnmappedToPlanned = async (itemNumber, itemName) => {
    // فتح المودال — لا نعرض confirm مباشرة
    const target = unmapped?.rows?.find(r => String(r.item_number) === String(itemNumber));
    if (!target) return;
    // جلب التصنيفات إن لم تكن محمّلة بعد
    if (categories.length === 0) {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data || []);
      } catch { /* ignore */ }
    }
    setAddUnmappedModal({
      itemNumber: String(itemNumber),
      itemName,
      unit: target.unit || '',
      totalQty: target.total_quantity,
      totalCost: target.total_cost,
      mode: 'create_new',
      newMaterialCode: String(itemNumber),
      newMaterialName: itemName || '',
      newMaterialUnit: target.unit || '',
      newMaterialCategory: '',
      linkMaterialCode: '',
      linkSearch: '',
    });
  };

  const confirmAddUnmapped = async () => {
    const m = addUnmappedModal;
    if (!m) return;
    let payload = { mode: m.mode };
    if (m.mode === 'link_existing') {
      if (!m.linkMaterialCode) {
        toast.error('اختر مادة موجودة أولاً');
        return;
      }
      payload.material_code = m.linkMaterialCode;
    } else {
      payload.material_code = m.newMaterialCode?.trim() || m.itemNumber;
      payload.material_name = m.newMaterialName?.trim() || m.itemName;
      payload.unit = m.newMaterialUnit?.trim() || m.unit;
      payload.category = m.newMaterialCategory?.trim() || '';
    }
    setAddingToPlanned(m.itemNumber);
    try {
      const res = await axios.post(`${API}/projects/${id}/unmapped-imports/${m.itemNumber}/add-to-planned`, payload);
      toast.success(res.data?.message || 'تمت الإضافة');
      const proj = await axios.get(`${API}/projects/${id}`);
      setProject(proj.data);
      setBuildingMaterials(proj.data?.building_materials || []);
      await loadUnmapped();
      setAddUnmappedModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل في الإضافة');
    } finally {
      setAddingToPlanned(null);
    }
  };

  const handleDismissUnmapped = async (itemNumber, itemName) => {
    if (!window.confirm(`تجاهل "${itemName}" وإزالته من القائمة (بدون إضافة للمبدئية)؟`)) return;
    try {
      await axios.delete(`${API}/projects/${id}/unmapped-imports/${itemNumber}`);
      toast.success('تم التجاهل');
      await loadUnmapped();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل في التجاهل');
    }
  };

  const buildFilterQS = () => {
    const params = new URLSearchParams();
    if (filterSupplier) params.append('supplier', filterSupplier);
    if (filterCategory) params.append('category', filterCategory);
    return params.toString() ? `?${params}` : '';
  };

  const handleExportComparisonExcel = async () => {
    try {
      toast.info('جاري إنشاء ملف Excel...');
      const response = await axios.get(`${API}/projects/${id}/comparison-report/excel${buildFilterQS()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = filterSupplier ? `_${filterSupplier}` : (filterCategory ? `_${filterCategory}` : '');
      link.setAttribute('download', `comparison_${project?.name || id}${suffix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('تم تحميل ملف Excel');
    } catch {
      toast.error('فشل في التصدير');
    }
  };

  const handleExportComparisonPdf = async (scope = 'current') => {
    // scope: 'current' (الفلتر الحالي), 'all' (الكل), 'each-supplier' (PDF لكل مورد)
    try {
      let qs = '';
      if (scope === 'current') qs = buildFilterQS();
      else if (scope === 'all') qs = '';
      toast.info('جاري إنشاء ملف PDF...');
      const response = await axios.get(`${API}/projects/${id}/comparison-report/pdf${qs}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const suffix = scope === 'current' && (filterSupplier || filterCategory) ? `_${filterSupplier || filterCategory}` : '';
      link.setAttribute('download', `comparison_${project?.name || id}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('تم تحميل ملف PDF');
    } catch {
      toast.error('فشل في إنشاء PDF');
    }
  };

  // قوائم الفلترة المتاحة
  const availableSuppliers = Array.from(new Set(purchases.map(p => (p.supplier || '').trim()).filter(Boolean))).sort();
  const availableCategories = Array.from(new Set(materials.map(m => (m.category || '').trim()).filter(Boolean))).sort();
  const filteredPurchases = purchases.filter(p => {
    if (filterSupplier && (p.supplier || '').trim() !== filterSupplier) return false;
    if (filterCategory) {
      const mat = materials.find(m => m.code === p.material_code);
      if (!mat || mat.category !== filterCategory) return false;
    }
    return true;
  });



  // Floors Management (إدارة الأدوار)
  const handleAddFloor = async () => {
    if (!newFloor.floor_name?.trim()) {
      toast.error('الرجاء إدخال اسم الدور');
      return;
    }
    if (!newFloor.area) {
      toast.error('الرجاء إدخال مساحة الدور');
      return;
    }
    try {
      const floors = [...(project.floors || []), newFloor];
      // Update total area
      const totalArea = floors.reduce((sum, f) => sum + (f.area || 0), 0);
      await axios.put(`${API}/projects/${id}`, { 
        floors, 
        total_area: totalArea,
        floors_count: floors.length 
      });
      toast.success('تم إضافة الدور');
      setShowAddFloor(false);
      setNewFloor({ floor_number: floors.length, floor_name: '', area: 0, steel_factor: 120 });
      fetchData();
    } catch (error) {
      toast.error('خطأ في الإضافة');
    }
  };

  const handleDeleteFloor = async (floorIndex) => {
    try {
      const floors = project.floors.filter((_, idx) => idx !== floorIndex);
      const totalArea = floors.reduce((sum, f) => sum + (f.area || 0), 0);
      await axios.put(`${API}/projects/${id}`, { 
        floors, 
        total_area: totalArea,
        floors_count: floors.length 
      });
      toast.success('تم حذف الدور');
      fetchData();
    } catch (error) {
      toast.error('خطأ في الحذف');
    }
  };

  const handleUpdateFloor = async (floorIndex, updatedFloor) => {
    try {
      const floors = [...project.floors];
      floors[floorIndex] = updatedFloor;
      const totalArea = floors.reduce((sum, f) => sum + (f.area || 0), 0);
      await axios.put(`${API}/projects/${id}`, { 
        floors, 
        total_area: totalArea 
      });
      toast.success('تم تحديث الدور');
      fetchData();
    } catch (error) {
      toast.error('خطأ في التحديث');
    }
  };

  // Export functions
  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/export/boq-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOQ_${project?.name || 'project'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('تم تحميل التقرير');
    } catch (error) {
      toast.error('خطأ في التصدير');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/export/boq-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOQ_${project?.name || 'project'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('تم تحميل التقرير');
    } catch (error) {
      toast.error('خطأ في التصدير');
    }
  };

  // Supply tracking
  const handleInitSupply = async () => {
    // جمع مواد الكميات (من النماذج)
    const quantityMaterials = calculation?.materials?.map(mat => ({
      material_id: mat.material_id,
      material_code: mat.material_code,
      material_name: mat.material_name,
      unit: mat.unit,
      required_quantity: mat.quantity,
      received_quantity: 0,
      unit_price: mat.unit_price,
      source: 'quantity'
    })) || [];
    
    // جمع مواد المساحة - تجميع المواد المتكررة حسب الكود
    const areaMaterialsRaw = calculation?.area_materials || [];
    const areaMaterialsMap = {};
    areaMaterialsRaw.forEach(mat => {
      const code = mat.material_code;
      if (!areaMaterialsMap[code]) {
        areaMaterialsMap[code] = {
          material_id: mat.id || mat.material_id || `area_${code}_${Date.now()}`,
          material_code: code,
          material_name: mat.material_name,
          unit: mat.unit,
          required_quantity: 0,
          received_quantity: 0,
          unit_price: mat.unit_price,
          source: 'area'
        };
      }
      // تجميع الكميات للمواد المتكررة
      areaMaterialsMap[code].required_quantity += (mat.quantity || 0);
    });
    const areaMaterials = Object.values(areaMaterialsMap);
    
    // جمع الكميات المبدئية (على مستوى العمارة الكلية) — من state buildingMaterials
    const buildingWideMaterials = (buildingMaterials || []).map(mat => ({
      material_id: mat.material_id || mat.id,
      material_code: mat.material_code,
      material_name: mat.material_name,
      unit: mat.unit,
      required_quantity: mat.quantity || 0,
      received_quantity: 0,
      unit_price: mat.unit_price || 0,
      source: 'building'
    }));
    
    // دمج كل المواد وإزالة التكرارات حسب الكود
    const allMaterialsMap = {};
    [...quantityMaterials, ...areaMaterials, ...buildingWideMaterials].forEach(mat => {
      const code = mat.material_code;
      if (!allMaterialsMap[code]) {
        allMaterialsMap[code] = mat;
      } else {
        // تجميع الكميات إذا كان نفس الكود من مصادر مختلفة
        allMaterialsMap[code].required_quantity += mat.required_quantity;
      }
    });
    const allSupplyItems = Object.values(allMaterialsMap);
    
    if (allSupplyItems.length === 0) {
      toast.error('لا توجد مواد لتتبعها');
      return;
    }
    
    try {
      // upsert جماعي في طلب واحد (سريع جداً حتى لمئات المواد)
      const res = await axios.post(`${API}/projects/${id}/supply/bulk`, { items: allSupplyItems });
      const d = res.data || {};
      toast.success(d.message || `تم تهيئة تتبع التوريد (${allSupplyItems.length} مادة)`);
      fetchData();
    } catch (error) {
      toast.error('خطأ في التهيئة');
    }
  };

  // تحديث/مزامنة تتبع التوريد (إضافة المواد الجديدة فقط)
  const handleSyncSupply = async () => {
    // جمع كل المواد الحالية من الحسابات
    const quantityMaterials = calculation?.materials?.map(mat => ({
      material_id: mat.material_id,
      material_code: mat.material_code,
      material_name: mat.material_name,
      unit: mat.unit,
      required_quantity: mat.quantity,
      received_quantity: 0,
      unit_price: mat.unit_price,
      source: 'quantity'
    })) || [];
    
    // مواد المساحة - تجميع المواد المتكررة حسب الكود
    const areaMaterialsRaw = calculation?.area_materials || [];
    const areaMaterialsMap = {};
    areaMaterialsRaw.forEach(mat => {
      const code = mat.material_code;
      if (!areaMaterialsMap[code]) {
        areaMaterialsMap[code] = {
          material_id: mat.id || mat.material_id || `area_${code}_${Date.now()}`,
          material_code: code,
          material_name: mat.material_name,
          unit: mat.unit,
          required_quantity: 0,
          received_quantity: 0,
          unit_price: mat.unit_price,
          source: 'area'
        };
      }
      areaMaterialsMap[code].required_quantity += (mat.quantity || 0);
    });
    const areaMaterials = Object.values(areaMaterialsMap);
    
    // جمع الكميات المبدئية (على مستوى العمارة الكلية) — من state buildingMaterials
    const buildingWideMaterials = (buildingMaterials || []).map(mat => ({
      material_id: mat.material_id || mat.id,
      material_code: mat.material_code,
      material_name: mat.material_name,
      unit: mat.unit,
      required_quantity: mat.quantity || 0,
      received_quantity: 0,
      unit_price: mat.unit_price || 0,
      source: 'building'
    }));
    
    // دمج كل المواد وإزالة التكرارات حسب الكود
    const allMaterialsMap = {};
    [...quantityMaterials, ...areaMaterials, ...buildingWideMaterials].forEach(mat => {
      const code = mat.material_code;
      if (!allMaterialsMap[code]) {
        allMaterialsMap[code] = mat;
      } else {
        allMaterialsMap[code].required_quantity += mat.required_quantity;
      }
    });
    const allMaterials = Object.values(allMaterialsMap);
    
    // إيجاد المواد التي لم تُضَف بعد لتتبع التوريد (حسب الكود وليس الـ ID)
    const existingCodes = supplyStatus.map(s => s.material_code);
    const newMaterials = allMaterials.filter(m => !existingCodes.includes(m.material_code));
    
    if (newMaterials.length === 0) {
      toast.info('جميع المواد موجودة في تتبع التوريد');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/projects/${id}/supply/bulk`, { items: newMaterials });
      const d = res.data || {};
      toast.success(d.message || `تم إضافة ${newMaterials.length} مادة جديدة لتتبع التوريد`);
      fetchData();
    } catch (error) {
      toast.error('خطأ في المزامنة');
    }
  };

  const handleUpdateReceived = async (materialId) => {
    try {
      await axios.put(`${API}/projects/${id}/supply/${materialId}`, { received_quantity: receivedQty });
      toast.success('تم التحديث');
      setEditingSupply(null);
      fetchData();
    } catch (error) {
      toast.error('خطأ في التحديث');
    }
  };

  // تصدير تتبع التوريد
  const handleExportSupply = async (format, type) => {
    try {
      const typeLabels = {
        'all': 'جميع المواد',
        'quantity': 'مواد الكميات',
        'area': 'مواد المساحة'
      };
      toast.info(`جاري تصدير ${typeLabels[type]}...`);
      
      const response = await axios.get(
        `${API}/projects/${id}/supply/export/${format}?type=${type}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      link.setAttribute('download', `تتبع_التوريد_${typeLabels[type]}_${project?.name || 'project'}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم التصدير بنجاح');
    } catch (error) {
      toast.error('خطأ في التصدير');
      console.error(error);
    }
  };

  // تصفية مواد التوريد حسب البحث والحالة
  const getFilteredSupplyItems = () => {
    let filtered = supplyStatus;
    
    // البحث
    if (supplySearch.trim()) {
      const searchLower = supplySearch.toLowerCase();
      filtered = filtered.filter(item => 
        item.material_name?.toLowerCase().includes(searchLower) ||
        item.material_code?.toLowerCase().includes(searchLower)
      );
    }
    
    // الفلتر حسب الحالة
    if (supplyFilter !== 'all') {
      filtered = filtered.filter(item => {
        const received = item.received_quantity || 0;
        const required = item.required_quantity || 0;
        
        if (supplyFilter === 'not_received') {
          return received === 0;
        } else if (supplyFilter === 'partial') {
          return received > 0 && received < required;
        } else if (supplyFilter === 'complete') {
          return received >= required;
        }
        return true;
      });
    }
    
    return filtered;
  };

  const filteredSupply = getFilteredSupplyItems();
  const totalSupplyPages = Math.ceil(filteredSupply.length / SUPPLY_PER_PAGE);
  const paginatedSupply = filteredSupply.slice(
    (supplyPage - 1) * SUPPLY_PER_PAGE,
    supplyPage * SUPPLY_PER_PAGE
  );

  // إحصائيات التوريد
  const supplyStats = {
    total: supplyStatus.length,
    notReceived: supplyStatus.filter(s => (s.received_quantity || 0) === 0).length,
    partial: supplyStatus.filter(s => {
      const r = s.received_quantity || 0;
      const req = s.required_quantity || 0;
      return r > 0 && r < req;
    }).length,
    complete: supplyStatus.filter(s => (s.received_quantity || 0) >= (s.required_quantity || 0)).length
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">المشروع غير موجود</p>
        <Button onClick={() => navigate('/buildings/projects')} className="mt-4">
          العودة للمشاريع
        </Button>
      </div>
    );
  }

  const tabs = [
    { id: 'floors', label: 'الأدوار', shortLabel: 'أدوار', icon: Layers },
    { id: 'templates', label: 'نماذج الشقق', shortLabel: 'نماذج', icon: Home },
    { id: 'area-materials', label: 'مواد المساحة', shortLabel: 'مساحة', icon: Weight },
    { id: 'building-materials', label: 'كميات مبدئية', shortLabel: 'مبدئية', icon: Package },
    { id: 'purchases', label: 'المشتريات الفعلية', shortLabel: 'مشتريات', icon: Truck },
    { id: 'unplanned', label: 'مشتريات غير مخططة', shortLabel: 'غير مخطط', icon: Package },
    { id: 'timeline', label: 'الجدول الزمني', shortLabel: 'الجدول الزمني', icon: Calendar },
    { id: 'comparison', label: 'تقرير المقارنة', shortLabel: 'مقارنة', icon: Weight },
    { id: 'boq', label: 'جدول الكميات', shortLabel: 'كميات', icon: Package },
    { id: 'steel', label: 'الحديد', shortLabel: 'حديد', icon: Weight },
    { id: 'supply', label: 'تتبع التوريد', shortLabel: 'توريد', icon: Truck },
  ];

  const totalUnits = project.unit_templates?.reduce((sum, t) => sum + (t.count || 0), 0) || 0;

  return (
    <div className="min-h-screen">
      {/* Hidden file input for project import */}
      <input
        type="file"
        ref={projectImportRef}
        onChange={handleImportProjectExcel}
        accept=".xlsx,.xls"
        className="hidden"
      />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-amber-600" />
            {project.name}
          </h1>
          {project.location && <p className="text-sm text-gray-500 mt-1">{project.location}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleExportProjectExcel} variant="outline" size="sm" className="gap-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">تصدير مشروع</span>
          </Button>
          <Button onClick={() => projectImportRef.current?.click()} variant="outline" size="sm" className="gap-1 text-blue-600 border-blue-300 hover:bg-blue-50">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">استيراد</span>
          </Button>
          <Button onClick={handleExportPDF} variant="outline" size="sm" className="gap-1">
            <FileText className="w-4 h-4" />
            PDF
          </Button>
          <Button onClick={handleExportExcel} variant="outline" size="sm" className="gap-1">
            <Download className="w-4 h-4" />
            Excel
          </Button>
          <Button onClick={() => navigate('/buildings/projects')} variant="outline" size="sm" className="gap-1">
            <ArrowRight className="w-4 h-4" />
            العودة
          </Button>
        </div>
      </div>

      {/* Quick Stats - محسّن للموبايل */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3 mb-4">
        <div className="bg-indigo-50 p-2 sm:p-3 rounded-lg text-center">
          <p className="text-[10px] sm:text-xs text-gray-500">الأدوار</p>
          <p className="text-lg sm:text-2xl font-bold text-indigo-600">{project.floors?.length || 0}</p>
        </div>
        <div className="bg-amber-50 p-2 sm:p-3 rounded-lg text-center">
          <p className="text-[10px] sm:text-xs text-gray-500">الشقق</p>
          <p className="text-lg sm:text-2xl font-bold text-amber-600">{totalUnits}</p>
        </div>
        <div className="bg-blue-50 p-2 sm:p-3 rounded-lg text-center">
          <p className="text-[10px] sm:text-xs text-gray-500">النماذج</p>
          <p className="text-lg sm:text-2xl font-bold text-blue-600">{project.unit_templates?.length || 0}</p>
        </div>
        <div className="bg-purple-50 p-2 sm:p-3 rounded-lg text-center col-span-3 sm:col-span-1">
          <p className="text-[10px] sm:text-xs text-gray-500">المساحة</p>
          <p className="text-lg sm:text-2xl font-bold text-purple-600">{project.total_area?.toLocaleString() || 0} <span className="text-sm">م²</span></p>
        </div>
        <div className="bg-emerald-50 p-2 sm:p-3 rounded-lg text-center hidden sm:block">
          <p className="text-[10px] sm:text-xs text-gray-500">الحديد</p>
          <p className="text-lg sm:text-2xl font-bold text-emerald-600">{calculation?.steel_calculation?.total_steel_tons?.toFixed(1) || 0} <span className="text-sm">طن</span></p>
        </div>
      </div>

      {/* Tabs - محسّن للموبايل */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            onClick={() => setActiveTab(tab.id)}
            size="sm"
            className={`gap-1.5 whitespace-nowrap flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 ${activeTab === tab.id ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
          >
            <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.shortLabel || tab.label}</span>
          </Button>
        ))}
      </div>

      {/* Floors Tab (الأدوار) */}
      {activeTab === 'floors' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white p-4 rounded-t-xl">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="w-5 h-5" />
                إدارة الأدوار
              </CardTitle>
              <Button 
                onClick={() => {
                  setNewFloor({ 
                    floor_number: project.floors?.length || 0, 
                    floor_name: '', 
                    area: 0, 
                    steel_factor: 120 
                  });
                  setShowAddFloor(true);
                }} 
                variant="secondary" 
                size="sm" 
                className="gap-1"
              >
                <Plus className="w-4 h-4" />
                إضافة دور
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Info Box */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <p className="font-bold mb-1">💡 إدارة الأدوار</p>
              <p>أضف الأدوار مع مساحة كل دور. المساحة الإجمالية للمشروع = مجموع مساحات الأدوار</p>
              <p className="mt-1">المساحة الإجمالية الحالية: <span className="font-bold">{project.total_area?.toLocaleString() || 0} م²</span></p>
            </div>

            {/* Add Floor Form */}
            {showAddFloor && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-indigo-300">
                <h4 className="font-bold mb-3">إضافة دور جديد</h4>
                <div className="grid md:grid-cols-4 gap-3 mb-3">
                  <div>
                    <Label className="text-xs">رقم الدور</Label>
                    <select
                      value={newFloor.floor_number}
                      onChange={(e) => setNewFloor(prev => ({ ...prev, floor_number: parseInt(e.target.value) }))}
                      className="w-full border rounded-md p-2 text-sm"
                    >
                      <option value={-1}>اللبشة (الأساسات)</option>
                      <option value={0}>الدور الأرضي</option>
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <option key={n} value={n}>الدور {n}</option>
                      ))}
                      <option value={99}>السطح</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">اسم الدور <span className="text-red-500">*</span></Label>
                    <Input
                      value={newFloor.floor_name}
                      onChange={(e) => setNewFloor(prev => ({ ...prev, floor_name: e.target.value }))}
                      placeholder="مثال: اللبشة، الدور الأرضي، الميزانين"
                      className="h-9"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs">المساحة (م²) <span className="text-red-500">*</span></Label>
                    <Input
                      type="number"
                      value={newFloor.area}
                      onChange={(e) => setNewFloor(prev => ({ ...prev, area: parseFloat(e.target.value) || 0 }))}
                      placeholder="مثال: 400"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">معامل الحديد (كجم/م²)</Label>
                    <Input
                      type="number"
                      value={newFloor.steel_factor}
                      onChange={(e) => setNewFloor(prev => ({ ...prev, steel_factor: parseFloat(e.target.value) || 120 }))}
                      placeholder="120"
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddFloor} className="bg-indigo-600 hover:bg-indigo-700 gap-1">
                    <Check className="w-4 h-4" />
                    إضافة
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddFloor(false)}>
                    إلغاء
                  </Button>
                </div>
              </div>
            )}

            {/* Floors List */}
            {project.floors?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-indigo-100">
                      <th className="p-3 text-right">الدور</th>
                      <th className="p-3 text-right">الاسم</th>
                      <th className="p-3 text-center">المساحة (م²)</th>
                      <th className="p-3 text-center">معامل الحديد</th>
                      <th className="p-3 text-center">كمية الحديد (كجم)</th>
                      <th className="p-3 text-center">كمية الحديد (طن)</th>
                      <th className="p-3 text-center w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.floors
                      .sort((a, b) => a.floor_number - b.floor_number)
                      .map((floor, idx) => {
                        const steelKg = floor.area * (floor.steel_factor || 120);
                        const steelTons = steelKg / 1000;
                        const floorLabel = floor.floor_number === -1 ? 'اللبشة' :
                                          floor.floor_number === 0 ? 'الأرضي' :
                                          floor.floor_number === 99 ? 'السطح' :
                                          `الدور ${floor.floor_number}`;
                        return (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                floor.floor_number === -1 ? 'bg-orange-100 text-orange-700' :
                                floor.floor_number === 0 ? 'bg-green-100 text-green-700' :
                                floor.floor_number === 99 ? 'bg-gray-100 text-gray-700' :
                                'bg-indigo-100 text-indigo-700'
                              }`}>
                                {floorLabel}
                              </span>
                            </td>
                            <td className="p-3 text-gray-600">{floor.floor_name || '-'}</td>
                            <td className="p-3 text-center font-bold text-blue-600">{floor.area?.toLocaleString()}</td>
                            <td className="p-3 text-center">{floor.steel_factor || 120}</td>
                            <td className="p-3 text-center font-bold">{steelKg.toLocaleString()}</td>
                            <td className="p-3 text-center font-bold text-emerald-600">{steelTons.toFixed(2)}</td>
                            <td className="p-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteFloor(idx)}
                                className="text-red-500 h-7 w-7 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-indigo-100 font-bold">
                      <td colSpan="2" className="p-3 text-right">الإجمالي</td>
                      <td className="p-3 text-center text-blue-700">
                        {project.floors.reduce((sum, f) => sum + (f.area || 0), 0).toLocaleString()} م²
                      </td>
                      <td className="p-3 text-center">-</td>
                      <td className="p-3 text-center">
                        {project.floors.reduce((sum, f) => sum + (f.area * (f.steel_factor || 120)), 0).toLocaleString()} كجم
                      </td>
                      <td className="p-3 text-center text-emerald-700">
                        {(project.floors.reduce((sum, f) => sum + (f.area * (f.steel_factor || 120)), 0) / 1000).toFixed(2)} طن
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Layers className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>لم تتم إضافة أدوار بعد</p>
                <p className="text-sm mt-1">أضف اللبشة، الأدوار، والسطح</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Templates List */}
          <div className="lg:col-span-1">
            <Card className="shadow-md border-0">
              <CardHeader className="bg-gray-50 p-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">نماذج الشقق ({project.unit_templates?.length || 0})</CardTitle>
                  <Button onClick={() => setShowAddTemplate(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7">
                    <Plus className="w-3 h-3 ml-1" />
                    جديد
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {project.unit_templates?.map((template) => (
                  <div
                    key={template.id}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTemplate?.id === template.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-amber-300'
                    }`}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-xs font-mono text-amber-600">{template.code}</span>
                        <h3 className="font-bold text-gray-800">{template.name}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {template.count} شقة • {template.materials?.length || 0} مادة
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }}
                        className="text-red-500 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {(!project.unit_templates || project.unit_templates.length === 0) && (
                  <p className="text-center text-gray-400 py-4">لا توجد نماذج</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Template Details */}
          <div className="lg:col-span-2">
            {selectedTemplate ? (
              <Card className="shadow-md border-0">
                <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 rounded-t-xl">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">{selectedTemplate.name}</CardTitle>
                    <Button onClick={handleUpdateTemplate} variant="secondary" size="sm" className="gap-1">
                      <Save className="w-4 h-4" />
                      حفظ
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Template Info */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                    <div>
                      <Label className="text-xs">الكود</Label>
                      <Input
                        value={selectedTemplate.code}
                        onChange={(e) => setSelectedTemplate({...selectedTemplate, code: e.target.value})}
                        className="h-9 font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">عدد الشقق</Label>
                      <Input
                        type="number"
                        value={selectedTemplate.count}
                        onChange={(e) => setSelectedTemplate({...selectedTemplate, count: parseInt(e.target.value) || 0})}
                        className="h-9 font-bold text-amber-600"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">المساحة (م²)</Label>
                      <Input
                        type="number"
                        value={selectedTemplate.area}
                        onChange={(e) => setSelectedTemplate({...selectedTemplate, area: parseFloat(e.target.value) || 0})}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">الغرف</Label>
                      <Input
                        type="number"
                        value={selectedTemplate.rooms_count}
                        onChange={(e) => setSelectedTemplate({...selectedTemplate, rooms_count: parseInt(e.target.value) || 0})}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">الحمامات</Label>
                      <Input
                        type="number"
                        value={selectedTemplate.bathrooms_count}
                        onChange={(e) => setSelectedTemplate({...selectedTemplate, bathrooms_count: parseInt(e.target.value) || 0})}
                        className="h-9"
                      />
                    </div>
                  </div>

                  {/* Add Material */}
                  <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <Label className="text-sm font-bold mb-2 block">إضافة مادة</Label>
                    <div className="flex gap-2 flex-wrap items-end">
                      <div className="flex-1 min-w-[250px]">
                        <Input
                          type="text"
                          placeholder="🔍 ابحث عن مادة بالاسم أو الكود..."
                          value={unitMaterialSearch}
                          onChange={(e) => setUnitMaterialSearch(e.target.value)}
                          className="h-9 mb-1"
                        />
                        <select
                          value={selectedMaterial}
                          onChange={(e) => setSelectedMaterial(e.target.value)}
                          className="h-9 w-full rounded-md border px-2 text-sm"
                          size={unitMaterialSearch ? Math.min(filteredUnitMaterials.length + 1, 6) : 1}
                        >
                          <option value="">اختر مادة وحدات... ({filteredUnitMaterials.length})</option>
                          {filteredUnitMaterials.map(mat => (
                            <option key={mat.id} value={mat.id}>
                              {mat.code} - {mat.name} ({mat.unit})
                              {mat.material_type === 'unit' ? ' 🏠' : ' 🔄'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Input
                        type="number"
                        placeholder="الكمية"
                        value={materialQty}
                        onChange={(e) => setMaterialQty(parseFloat(e.target.value) || 1)}
                        className="h-9 w-24"
                      />
                      <Button onClick={handleAddMaterialToTemplate} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {filteredUnitMaterials.length === 0 && unitMaterialSearch && (
                      <p className="text-xs text-orange-600 mt-1">⚠️ لم يتم العثور على مواد تطابق &quot;{unitMaterialSearch}&quot;</p>
                    )}
                    {materials.filter(m => m.material_type === 'unit' || m.material_type === 'shared').length === 0 && (
                      <p className="text-xs text-orange-600 mt-1">⚠️ لا توجد مواد وحدات. قم بتصنيف المواد من صفحة المواد الأساسية</p>
                    )}
                  </div>

                  {/* Materials List */}
                  <div>
                    <Label className="text-sm font-bold mb-2 block flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      مواد النموذج ({selectedTemplate.materials?.length || 0})
                    </Label>
                    {selectedTemplate.materials?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="p-2 text-right">الكود</th>
                              <th className="p-2 text-right">المادة</th>
                              <th className="p-2 text-center">الكمية/شقة</th>
                              <th className="p-2 text-center">الوحدة</th>
                              <th className="p-2 text-center">السعر</th>
                              <th className="p-2 text-center w-16"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedTemplate.materials.map((mat, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="p-2">
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded font-mono text-xs">
                                    {mat.material_code}
                                  </span>
                                </td>
                                <td className="p-2 font-medium">{mat.material_name}</td>
                                <td className="p-2 text-center font-bold">{mat.quantity_per_unit}</td>
                                <td className="p-2 text-center text-gray-500">{mat.unit}</td>
                                <td className="p-2 text-center">{mat.unit_price?.toLocaleString()}</td>
                                <td className="p-2 text-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveMaterialFromTemplate(idx)}
                                    className="text-red-500 h-7 w-7 p-0"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center text-gray-400 py-4">لم تتم إضافة مواد بعد</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="shadow-md border-0">
                <CardContent className="p-8 text-center">
                  <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">اختر نموذجاً لعرض التفاصيل أو أنشئ نموذجاً جديداً</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Area Materials Tab (مواد المساحة) */}
      {activeTab === 'area-materials' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gradient-to-r from-violet-500 to-purple-600 text-white p-4 rounded-t-xl">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <Weight className="w-5 h-5" />
                مواد المساحة (حديد، بلك، إلخ)
              </CardTitle>
              <Button 
                onClick={() => setShowAddAreaMaterial(true)} 
                variant="secondary" 
                size="sm" 
                className="gap-1"
              >
                <Plus className="w-4 h-4" />
                إضافة مادة
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="mb-4 p-3 bg-purple-50 rounded-lg text-sm text-purple-800">
              <p className="font-bold mb-1">💡 مواد المساحة</p>
              <p>هذه المواد تُحسب حسب المساحة الإجمالية للمشروع × المعامل (مثل: 120 كجم حديد/م²)</p>
              <p className="mt-1">المساحة الإجمالية للمشروع: <span className="font-bold">{project.total_area?.toLocaleString()} م²</span></p>
            </div>

            {/* Add Area Material Form */}
            {showAddAreaMaterial && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-purple-300">
                <h4 className="font-bold mb-3">إضافة مادة مساحة جديدة</h4>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                  <div>
                    <Label className="text-xs">اختر المادة</Label>
                    <Input
                      type="text"
                      placeholder="🔍 ابحث عن مادة..."
                      value={areaMaterialSearch}
                      onChange={(e) => setAreaMaterialSearch(e.target.value)}
                      className="h-9 mb-1"
                    />
                    <select
                      value={newAreaMaterial.material_id}
                      onChange={(e) => handleSelectAreaMaterial(e.target.value)}
                      className="w-full border rounded-md p-2 text-sm"
                      size={areaMaterialSearch ? Math.min(filteredAreaMaterials.length + 1, 6) : 1}
                    >
                      <option value="">-- اختر مادة مساحة ({filteredAreaMaterials.length}) --</option>
                      {filteredAreaMaterials.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.code} - {m.name}
                          {m.material_type === 'area' ? ' 📐' : ' 🔄'}
                        </option>
                      ))}
                    </select>
                    {filteredAreaMaterials.length === 0 && areaMaterialSearch && (
                      <p className="text-xs text-orange-600 mt-1">⚠️ لم يتم العثور على مواد تطابق البحث</p>
                    )}
                    {materials.filter(m => m.material_type === 'area' || m.material_type === 'shared').length === 0 && (
                      <p className="text-xs text-orange-600 mt-1">⚠️ لا توجد مواد مساحة. قم بتصنيف المواد من صفحة المواد الأساسية</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">المعامل ({newAreaMaterial.unit}/م²)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newAreaMaterial.factor}
                      onChange={(e) => setNewAreaMaterial(prev => ({ ...prev, factor: parseFloat(e.target.value) || 0 }))}
                      placeholder="مثال: 120 كجم/م²"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">سعر الوحدة</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newAreaMaterial.unit_price}
                      onChange={(e) => setNewAreaMaterial(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">طريقة الحساب</Label>
                    <select
                      value={newAreaMaterial.calculation_type}
                      onChange={(e) => setNewAreaMaterial(prev => ({ ...prev, calculation_type: e.target.value, selected_floors: [] }))}
                      className="w-full border rounded-md p-2 text-sm"
                    >
                      <option value="all_floors">جميع الأدوار</option>
                      <option value="selected_floors">أدوار محددة</option>
                    </select>
                  </div>
                </div>

                {/* نسبة الهالك - تنطبق على جميع المواد */}
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-xs">نسبة الهالك (%) - اختياري</Label>
                    <Input
                      type="number"
                      step="1"
                      value={newAreaMaterial.waste_percentage || ''}
                      onChange={(e) => setNewAreaMaterial(prev => ({ ...prev, waste_percentage: parseFloat(e.target.value) || 0 }))}
                      placeholder="مثال: 10"
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Tile Size - اختياري للبلاط فقط */}
                <details className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <summary className="cursor-pointer text-xs text-blue-700 font-bold">📐 مقاس البلاط/اللوح (اختياري - للبلاط والجبسن بورد فقط)</summary>
                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label className="text-xs">عرض البلاطة (سم)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={newAreaMaterial.tile_width || ''}
                        onChange={(e) => setNewAreaMaterial(prev => ({ ...prev, tile_width: parseFloat(e.target.value) || 0 }))}
                        placeholder="مثال: 60"
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">طول البلاطة (سم)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={newAreaMaterial.tile_height || ''}
                        onChange={(e) => setNewAreaMaterial(prev => ({ ...prev, tile_height: parseFloat(e.target.value) || 0 }))}
                        placeholder="مثال: 60"
                        className="h-9"
                      />
                    </div>
                  </div>
                  {newAreaMaterial.tile_width > 0 && newAreaMaterial.tile_height > 0 && (
                    <div className="mt-2 text-xs text-blue-600">
                      <span className="font-bold">حساب تلقائي:</span> مساحة البلاطة = {((newAreaMaterial.tile_width * newAreaMaterial.tile_height) / 10000).toFixed(4)} م² | 
                      عدد البلاط/م² = {(10000 / (newAreaMaterial.tile_width * newAreaMaterial.tile_height)).toFixed(2)} بلاطة
                      {newAreaMaterial.waste_percentage > 0 && (
                        <span> | مع الهالك ({newAreaMaterial.waste_percentage}%) = {((10000 / (newAreaMaterial.tile_width * newAreaMaterial.tile_height)) * (1 + newAreaMaterial.waste_percentage / 100)).toFixed(2)} بلاطة/م²</span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">💡 عند إدخال مقاس البلاط، سيتم حساب الكمية بالمتر المربع مع الهالك، وعرض عدد البلاطات كمعلومة إضافية</p>
                </details>
                
                {/* Floor Selection */}
                {newAreaMaterial.calculation_type === 'selected_floors' && (
                  <div className="mb-3 p-3 bg-white rounded-lg border">
                    <Label className="text-xs font-bold text-purple-700 mb-2 block">اختر الأدوار:</Label>
                    {project.floors?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {project.floors
                          .sort((a, b) => a.floor_number - b.floor_number)
                          .map((floor) => {
                            const isSelected = newAreaMaterial.selected_floors?.includes(floor.floor_number);
                            return (
                              <button
                                key={floor.floor_number}
                                type="button"
                                onClick={() => handleToggleFloorSelection(floor.floor_number)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                  isSelected 
                                    ? 'bg-purple-600 text-white shadow-md' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {floor.floor_name || `دور ${floor.floor_number}`}
                                <span className="text-xs opacity-75 mr-1">({floor.area} م²)</span>
                              </button>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-sm text-orange-600">⚠️ لم تتم إضافة أدوار بعد. أضف الأدوار أولاً من تبويب الأدوار</p>
                    )}
                    {newAreaMaterial.selected_floors?.length > 0 && (
                      <p className="text-xs text-purple-600 mt-2">
                        الأدوار المختارة: {project.floors
                          ?.filter(f => newAreaMaterial.selected_floors?.includes(f.floor_number))
                          .map(f => f.floor_name || `دور ${f.floor_number}`)
                          .join('، ')} | 
                        المساحة: {project.floors
                          ?.filter(f => newAreaMaterial.selected_floors?.includes(f.floor_number))
                          .reduce((sum, f) => sum + (f.area || 0), 0)
                          .toLocaleString()} م²
                      </p>
                    )}
                  </div>
                )}
                
                <div className="mb-3">
                  <Label className="text-xs">ملاحظات</Label>
                  <Input
                    value={newAreaMaterial.notes}
                    onChange={(e) => setNewAreaMaterial(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="ملاحظات إضافية (اختياري)"
                    className="h-9"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddAreaMaterial} className="bg-purple-600 hover:bg-purple-700 gap-1">
                    <Check className="w-4 h-4" />
                    إضافة
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddAreaMaterial(false)}>
                    إلغاء
                  </Button>
                </div>
              </div>
            )}

            {/* Edit Area Material Form */}
            {editingAreaMaterial && (
              <div className="mb-4 p-4 bg-amber-50 rounded-lg border-2 border-amber-300">
                <h4 className="font-bold mb-3 flex items-center gap-2">
                  <Edit2 className="w-4 h-4" />
                  تعديل مادة: {editingAreaMaterial.material_name}
                </h4>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                  <div>
                    <Label className="text-xs">المادة</Label>
                    <Input
                      value={`${editingAreaMaterial.material_code} - ${editingAreaMaterial.material_name}`}
                      disabled
                      className="h-9 bg-gray-100"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">المعامل ({editingAreaMaterial.unit}/م²)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingAreaMaterial.factor}
                      onChange={(e) => setEditingAreaMaterial(prev => ({ ...prev, factor: parseFloat(e.target.value) || 0 }))}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">سعر الوحدة</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingAreaMaterial.unit_price}
                      onChange={(e) => setEditingAreaMaterial(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">طريقة الحساب</Label>
                    <select
                      value={editingAreaMaterial.calculation_type}
                      onChange={(e) => setEditingAreaMaterial(prev => ({ ...prev, calculation_type: e.target.value, selected_floors: e.target.value === 'all_floors' ? [] : prev.selected_floors }))}
                      className="w-full border rounded-md p-2 text-sm"
                    >
                      <option value="all_floors">جميع الأدوار</option>
                      <option value="selected_floors">أدوار محددة</option>
                    </select>
                  </div>
                </div>

                {/* نسبة الهالك - تنطبق على جميع المواد */}
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-xs">نسبة الهالك (%) - اختياري</Label>
                    <Input
                      type="number"
                      step="1"
                      value={editingAreaMaterial.waste_percentage || ''}
                      onChange={(e) => setEditingAreaMaterial(prev => ({ ...prev, waste_percentage: parseFloat(e.target.value) || 0 }))}
                      placeholder="مثال: 10"
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Tile Size - اختياري للبلاط فقط */}
                <details className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200" open={editingAreaMaterial.tile_width > 0}>
                  <summary className="cursor-pointer text-xs text-blue-700 font-bold">📐 مقاس البلاط/اللوح (اختياري - للبلاط والجبسن بورد فقط)</summary>
                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label className="text-xs">عرض البلاطة (سم)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={editingAreaMaterial.tile_width || ''}
                        onChange={(e) => setEditingAreaMaterial(prev => ({ ...prev, tile_width: parseFloat(e.target.value) || 0 }))}
                        placeholder="مثال: 60"
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">طول البلاطة (سم)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={editingAreaMaterial.tile_height || ''}
                        onChange={(e) => setEditingAreaMaterial(prev => ({ ...prev, tile_height: parseFloat(e.target.value) || 0 }))}
                        placeholder="مثال: 60"
                        className="h-9"
                      />
                    </div>
                  </div>
                  {editingAreaMaterial.tile_width > 0 && editingAreaMaterial.tile_height > 0 && (
                    <div className="mt-2 text-xs text-blue-600">
                      <span className="font-bold">حساب تلقائي:</span> مساحة البلاطة = {((editingAreaMaterial.tile_width * editingAreaMaterial.tile_height) / 10000).toFixed(4)} م² | 
                      عدد البلاط/م² = {(10000 / (editingAreaMaterial.tile_width * editingAreaMaterial.tile_height)).toFixed(2)} بلاطة
                      {editingAreaMaterial.waste_percentage > 0 && (
                        <span> | مع الهالك ({editingAreaMaterial.waste_percentage}%) = {((10000 / (editingAreaMaterial.tile_width * editingAreaMaterial.tile_height)) * (1 + editingAreaMaterial.waste_percentage / 100)).toFixed(2)} بلاطة/م²</span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">💡 عند إدخال مقاس البلاط، سيتم حساب الكمية بالمتر المربع مع الهالك، وعرض عدد البلاطات كمعلومة إضافية</p>
                </details>
                
                {/* Floor Selection for Edit */}
                {editingAreaMaterial.calculation_type === 'selected_floors' && (
                  <div className="mb-3 p-3 bg-white rounded-lg border">
                    <Label className="text-xs font-bold text-amber-700 mb-2 block">اختر الأدوار:</Label>
                    {project.floors?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {project.floors
                          .sort((a, b) => a.floor_number - b.floor_number)
                          .map((floor) => {
                            const isSelected = editingAreaMaterial.selected_floors?.includes(floor.floor_number);
                            return (
                              <button
                                key={floor.floor_number}
                                type="button"
                                onClick={() => handleToggleFloorSelectionEdit(floor.floor_number)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                  isSelected 
                                    ? 'bg-amber-600 text-white shadow-md' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {floor.floor_name || `دور ${floor.floor_number}`}
                                <span className="text-xs opacity-75 mr-1">({floor.area} م²)</span>
                              </button>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-sm text-orange-600">⚠️ لم تتم إضافة أدوار بعد</p>
                    )}
                    {editingAreaMaterial.selected_floors?.length > 0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        الأدوار المختارة: {project.floors
                          ?.filter(f => editingAreaMaterial.selected_floors?.includes(f.floor_number))
                          .map(f => f.floor_name || `دور ${f.floor_number}`)
                          .join('، ')} | 
                        المساحة: {project.floors
                          ?.filter(f => editingAreaMaterial.selected_floors?.includes(f.floor_number))
                          .reduce((sum, f) => sum + (f.area || 0), 0)
                          .toLocaleString()} م²
                      </p>
                    )}
                  </div>
                )}
                
                <div className="mb-3">
                  <Label className="text-xs">ملاحظات</Label>
                  <Input
                    value={editingAreaMaterial.notes}
                    onChange={(e) => setEditingAreaMaterial(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="ملاحظات إضافية (اختياري)"
                    className="h-9"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleUpdateAreaMaterial} className="bg-amber-600 hover:bg-amber-700 gap-1">
                    <Save className="w-4 h-4" />
                    حفظ التعديلات
                  </Button>
                  <Button variant="outline" onClick={() => setEditingAreaMaterial(null)}>
                    إلغاء
                  </Button>
                </div>
              </div>
            )}

            {/* Area Materials List */}
            {project.area_materials?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-purple-100">
                      <th className="p-2 text-right">الكود</th>
                      <th className="p-2 text-right">المادة</th>
                      <th className="p-2 text-center">المعامل/المقاس</th>
                      <th className="p-2 text-center">الهالك</th>
                      <th className="p-2 text-center">الأدوار</th>
                      <th className="p-2 text-center">المساحة</th>
                      <th className="p-2 text-center">الكمية</th>
                      <th className="p-2 text-center">السعر</th>
                      <th className="p-2 text-center">الإجمالي</th>
                      <th className="p-2 text-center w-24">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.area_materials.map((mat) => {
                      // حساب المساحة حسب الأدوار المحددة
                      let selectedArea = 0;
                      let floorsLabel = 'جميع الأدوار';
                      
                      if (mat.calculation_type === 'selected_floors' && mat.selected_floors?.length > 0) {
                        const selectedFloorsData = project.floors?.filter(f => mat.selected_floors.includes(f.floor_number)) || [];
                        selectedArea = selectedFloorsData.reduce((sum, f) => sum + (f.area || 0), 0);
                        // عرض أسماء الأدوار
                        floorsLabel = selectedFloorsData.map(f => f.floor_name || `دور ${f.floor_number}`).join('، ');
                      } else {
                        selectedArea = project.floors?.reduce((sum, f) => sum + (f.area || 0), 0) || project.total_area || 0;
                      }
                      
                      // حساب الكمية مع دعم البلاط
                      let quantity = 0;
                      let factorDisplay = mat.factor;
                      let tilesCount = 0;
                      let tilesPerSqm = 0;
                      let displayUnit = mat.unit || '';
                      
                      if (mat.tile_width > 0 && mat.tile_height > 0) {
                        // حساب البلاط: الكمية بالمتر المربع مع عرض عدد البلاطات كمعلومة إضافية
                        const tileArea = (mat.tile_width * mat.tile_height) / 10000; // تحويل من سم² إلى م²
                        tilesPerSqm = 1 / tileArea;
                        quantity = selectedArea; // الكمية بالمتر المربع
                        factorDisplay = `${mat.tile_width}×${mat.tile_height} سم`;
                        displayUnit = 'م²';
                      } else {
                        quantity = selectedArea * mat.factor;
                      }
                      
                      // إضافة نسبة الهالك
                      const wasteMultiplier = 1 + (mat.waste_percentage || 0) / 100;
                      const quantityWithWaste = quantity * wasteMultiplier;
                      
                      // حساب عدد البلاطات للعرض
                      if (mat.tile_width > 0 && mat.tile_height > 0) {
                        tilesCount = Math.ceil(quantityWithWaste * tilesPerSqm);
                      }
                      
                      const total = quantityWithWaste * mat.unit_price;
                      
                      return (
                        <tr key={mat.id} className="border-b hover:bg-gray-50">
                          <td className="p-2">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-mono text-xs">
                              {mat.material_code}
                            </span>
                          </td>
                          <td className="p-2 font-medium">{mat.material_name}</td>
                          <td className="p-2 text-center">
                            {mat.tile_width > 0 && mat.tile_height > 0 ? (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {factorDisplay}
                              </span>
                            ) : (
                              <span className="font-bold text-purple-600">{mat.factor}</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {mat.waste_percentage > 0 ? (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                {mat.waste_percentage}%
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              mat.calculation_type === 'selected_floors' 
                                ? 'bg-orange-100 text-orange-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {floorsLabel}
                            </span>
                          </td>
                          <td className="p-2 text-center text-gray-600">{selectedArea.toLocaleString()} م²</td>
                          <td className="p-2 text-center">
                            <div>
                              <span className="font-bold text-blue-600">
                                {quantityWithWaste.toLocaleString(undefined, {maximumFractionDigits: 2})} {displayUnit}
                              </span>
                              {mat.tile_width > 0 && mat.tile_height > 0 && tilesCount > 0 && (
                                <div className="text-xs text-purple-600 mt-0.5">
                                  ≈ {tilesCount.toLocaleString()} بلاطة
                                </div>
                              )}
                              {mat.waste_percentage > 0 && (
                                <div className="text-xs text-gray-400">
                                  (أصلي: {quantity.toLocaleString(undefined, {maximumFractionDigits: 2})})
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-2 text-center">{mat.unit_price?.toLocaleString()}</td>
                          <td className="p-2 text-center font-bold text-emerald-600">{total.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                          <td className="p-2 text-center">
                            <div className="flex gap-1 justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditAreaMaterial(mat)}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-7 w-7 p-0"
                                title="تعديل"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAreaMaterial(mat.id)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                title="حذف"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-purple-100 font-bold">
                      <td colSpan="8" className="p-2 text-right">إجمالي مواد المساحة:</td>
                      <td className="p-2 text-center text-purple-700">
                        {project.area_materials.reduce((sum, mat) => {
                          let selectedArea = 0;
                          if (mat.calculation_type === 'selected_floors' && mat.selected_floors?.length > 0) {
                            selectedArea = project.floors?.filter(f => mat.selected_floors.includes(f.floor_number)).reduce((s, f) => s + (f.area || 0), 0) || 0;
                          } else {
                            selectedArea = project.floors?.reduce((s, f) => s + (f.area || 0), 0) || project.total_area || 0;
                          }
                          // حساب الكمية مع دعم البلاط
                          let quantity = 0;
                          if (mat.tile_width > 0 && mat.tile_height > 0) {
                            const tileArea = (mat.tile_width * mat.tile_height) / 10000;
                            quantity = selectedArea / tileArea;
                          } else {
                            quantity = selectedArea * mat.factor;
                          }
                          // إضافة نسبة الهالك
                          const wasteMultiplier = 1 + (mat.waste_percentage || 0) / 100;
                          quantity = quantity * wasteMultiplier;
                          return sum + (quantity * mat.unit_price);
                        }, 0).toLocaleString(undefined, {maximumFractionDigits: 2})} ريال
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Weight className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>لم تتم إضافة مواد مساحة بعد</p>
                <p className="text-sm mt-1">أضف الحديد، البلك، الخرسانة، وغيرها</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Building-Wide Materials Tab (مواد العمارة الكلية) */}
      {activeTab === 'building-materials' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gray-50 p-4 flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-fuchsia-600" />
              مواد العمارة الكلية
            </CardTitle>
            <div className="flex items-center gap-2">
              <input
                ref={bmImportInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => handleImportBuildingMaterialsXlsx(e.target.files?.[0])}
                data-testid="import-bm-file-input"
              />
              <Button
                onClick={() => bmImportInputRef.current?.click()}
                disabled={bmImporting}
                size="sm"
                variant="outline"
                className="border-green-600 text-green-700 hover:bg-green-50"
                data-testid="import-bm-xlsx-btn"
                title="استيراد كميات مبدئية من ملف Excel — يستخدم الكود لمطابقة الأصناف"
              >
                {bmImporting ? (
                  <><span className="animate-spin ml-1">◜</span> جاري الاستيراد...</>
                ) : (
                  <><FileSpreadsheet className="w-4 h-4 ml-1" /> استيراد من Excel</>
                )}
              </Button>
              <Button
                onClick={() => setShowAddBuildingMaterial(true)}
                size="sm"
                className="bg-fuchsia-600 hover:bg-fuchsia-700"
                data-testid="add-building-material-btn"
              >
                <Plus className="w-4 h-4 ml-1" /> إضافة مادة
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Import Result Panel */}
            {bmImportResult && (
              <div className={`border-2 rounded-lg p-3 mb-4 ${bmImportResult.error ? 'bg-red-50 border-red-300' : 'bg-emerald-50 border-emerald-300'}`} data-testid="bm-import-result">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-sm">
                  {bmImportResult.error
                    ? '⚠️ فشل الاستيراد'
                    : (bmImportResult.updated_count > 0 && bmImportResult.added_count === 0)
                      ? `🔄 تم تحديث ${bmImportResult.updated_count} مادة`
                      : bmImportResult.updated_count > 0
                        ? `✓ ${bmImportResult.added_count} جديدة + ${bmImportResult.updated_count} محدَّثة`
                        : `✓ تم استيراد ${bmImportResult.imported_count || 0} مادة`}
                </div>
                  <button
                    onClick={() => setBmImportResult(null)}
                    className="text-gray-400 hover:text-gray-700"
                    data-testid="close-bm-import-result"
                    title="إغلاق"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-xs text-gray-700 space-y-1">
                  {bmImportResult.added_count > 0 && (
                    <div>➕ مواد جديدة مُضافة: <span className="font-bold text-emerald-700">{bmImportResult.added_count}</span></div>
                  )}
                  {bmImportResult.updated_count > 0 && (
                    <div>🔄 مواد محدَّثة (نفس الكود): <span className="font-bold text-blue-700">{bmImportResult.updated_count}</span></div>
                  )}
                  {(!bmImportResult.added_count && !bmImportResult.updated_count && bmImportResult.imported_count > 0) && (
                    <div>✅ عدد المواد المستوردة: <span className="font-bold text-emerald-700">{bmImportResult.imported_count}</span></div>
                  )}
                  {bmImportResult.skipped_section_headers > 0 && (
                    <div>ℹ️ تم تخطي {bmImportResult.skipped_section_headers} صف عناوين أقسام (بدون كمية)</div>
                  )}
                  {(bmImportResult.skipped_no_match_count || bmImportResult.total_no_match) > 0 && (
                    <div className="mt-2">
                      <div className="text-red-700 font-semibold mb-1">
                        ⚠️ {bmImportResult.skipped_no_match_count || bmImportResult.total_no_match} مادة لم يتم استيرادها لعدم مطابقة الكود في كتالوج المواد:
                      </div>
                      <div className="max-h-32 overflow-y-auto bg-white border rounded p-2 text-[11px]">
                        <table className="w-full">
                          <thead>
                            <tr className="text-gray-500">
                              <th className="text-right pb-1">الصف</th>
                              <th className="text-right pb-1">الكود</th>
                              <th className="text-right pb-1">الاسم</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(bmImportResult.skipped_no_match_sample || bmImportResult.skipped_no_match || []).map((r, i) => (
                              <tr key={i} className="border-t">
                                <td className="py-0.5">{r.row}</td>
                                <td className="py-0.5 font-mono">{r.code || '-'}</td>
                                <td className="py-0.5">{r.name}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        💡 أضف الكود الصحيح في عمود {'"'}الكود{'"'} في ملف Excel وأعد الاستيراد
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 text-xs text-fuchsia-900 mb-4">
              💡 هذه المواد تُحسب كمية واحدة ثابتة للعمارة كاملة (مثل المصاعد، مولِّد كهرباء، خزان مياه رئيسي) — بدون ربط بدور أو نموذج شقة.
            </div>

            {/* Add form */}
            {showAddBuildingMaterial && (
              <div className="bg-gradient-to-l from-fuchsia-50 to-pink-50 border-2 border-fuchsia-300 rounded-lg p-4 mb-4">
                <h3 className="font-bold text-fuchsia-900 mb-3">مادة جديدة على مستوى العمارة</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">المادة *</Label>
                    <select
                      value={newBuildingMaterial.material_id}
                      onChange={(e) => handleBuildingMaterialSelect(e.target.value)}
                      data-testid="new-bm-material-select"
                      className="w-full h-9 rounded-md border px-2 text-sm bg-white"
                    >
                      <option value="">اختر مادة...</option>
                      {materials.map(m => (
                        <option key={m.id} value={m.id}>{m.code} - {m.name} ({m.unit})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">الكمية الإجمالية * ({newBuildingMaterial.unit || '—'})</Label>
                    <Input
                      type="number" step="0.01"
                      value={newBuildingMaterial.quantity || ''}
                      onChange={(e) => setNewBuildingMaterial(p => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))}
                      data-testid="new-bm-quantity"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">سعر الوحدة</Label>
                    <Input
                      type="number" step="0.01"
                      value={newBuildingMaterial.unit_price || ''}
                      onChange={(e) => setNewBuildingMaterial(p => ({ ...p, unit_price: parseFloat(e.target.value) || 0 }))}
                      data-testid="new-bm-price"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">ملاحظات</Label>
                    <Input
                      value={newBuildingMaterial.notes}
                      onChange={(e) => setNewBuildingMaterial(p => ({ ...p, notes: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button onClick={handleAddBuildingMaterial} size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="save-bm-btn">حفظ</Button>
                  <Button onClick={() => { setShowAddBuildingMaterial(false); setNewBuildingMaterial({ material_id: '', material_code: '', material_name: '', unit: '', quantity: 0, unit_price: 0, notes: '' }); }} size="sm" variant="outline">إلغاء</Button>
                </div>
              </div>
            )}

            {/* Materials list */}
            {buildingMaterials.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="font-semibold">لا توجد مواد على مستوى العمارة</p>
                <p className="text-sm">اضغط «إضافة مادة» للبدء</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-fuchsia-100">
                    <tr>
                      <th className="p-2 text-right">الكود</th>
                      <th className="p-2 text-right">المادة</th>
                      <th className="p-2 text-center">الكمية</th>
                      <th className="p-2 text-center">الوحدة</th>
                      <th className="p-2 text-center">سعر الوحدة</th>
                      <th className="p-2 text-center">الإجمالي</th>
                      <th className="p-2 text-right">ملاحظات</th>
                      <th className="p-2 text-center w-24">إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildingMaterials.map(mat => {
                      const isEditing = editingBuildingMaterial?.id === mat.id;
                      const total = (mat.quantity || 0) * (mat.unit_price || 0);
                      return (
                        <tr key={mat.id} className="border-b hover:bg-gray-50">
                          <td className="p-2">
                            <span className="px-2 py-0.5 bg-fuchsia-100 text-fuchsia-700 rounded font-mono text-xs">{mat.material_code}</span>
                          </td>
                          <td className="p-2 font-medium">{mat.material_name}</td>
                          <td className="p-2 text-center">
                            {isEditing ? (
                              <Input type="number" step="0.01" value={editingBuildingMaterial.quantity} onChange={(e) => setEditingBuildingMaterial(p => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))} className="h-8 w-24 mx-auto" />
                            ) : (
                              <span className="font-bold text-fuchsia-700">{mat.quantity}</span>
                            )}
                          </td>
                          <td className="p-2 text-center text-gray-500">{mat.unit}</td>
                          <td className="p-2 text-center">
                            {isEditing ? (
                              <Input type="number" step="0.01" value={editingBuildingMaterial.unit_price} onChange={(e) => setEditingBuildingMaterial(p => ({ ...p, unit_price: parseFloat(e.target.value) || 0 }))} className="h-8 w-24 mx-auto" />
                            ) : (
                              <span>{(mat.unit_price || 0).toLocaleString()}</span>
                            )}
                          </td>
                          <td className="p-2 text-center font-bold text-emerald-700">{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="p-2 text-xs text-gray-500">
                            {isEditing ? (
                              <Input value={editingBuildingMaterial.notes} onChange={(e) => setEditingBuildingMaterial(p => ({ ...p, notes: e.target.value }))} className="h-8" />
                            ) : (mat.notes || '—')}
                          </td>
                          <td className="p-2 text-center">
                            {isEditing ? (
                              <div className="flex gap-1 justify-center">
                                <Button onClick={handleUpdateBuildingMaterial} size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700" data-testid={`save-bm-edit-${mat.id}`}>حفظ</Button>
                                <Button onClick={() => setEditingBuildingMaterial(null)} size="sm" variant="outline" className="h-7 px-2">إلغاء</Button>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-center">
                                <Button onClick={() => setEditingBuildingMaterial({ ...mat })} size="sm" variant="ghost" className="h-7 px-2" data-testid={`edit-bm-${mat.id}`}>تعديل</Button>
                                <Button onClick={() => handleDeleteBuildingMaterial(mat.id)} size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50" data-testid={`delete-bm-${mat.id}`}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-fuchsia-50 font-bold">
                      <td colSpan="5" className="p-2 text-left">إجمالي مواد العمارة:</td>
                      <td className="p-2 text-center text-emerald-700" data-testid="bm-total-cost">
                        {buildingMaterials.reduce((s, m) => s + (m.quantity || 0) * (m.unit_price || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* Purchases Tab (المشتريات الفعلية) */}
      {activeTab === 'purchases' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gray-50 p-4 flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="w-5 h-5 text-emerald-600" />
              المشتريات الفعلية ({purchases.length})
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <input ref={purchaseImportRef} type="file" accept=".xlsx,.xls" onChange={handleImportPurchasesExcel} className="hidden" />
              <Button onClick={() => purchaseImportRef.current?.click()} size="sm" variant="outline" data-testid="import-purchases-btn">
                <Upload className="w-4 h-4 ml-1" /> استيراد Excel
              </Button>
              <Button onClick={() => setShowAddPurchase(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-purchase-btn">
                <Plus className="w-4 h-4 ml-1" /> إضافة شراء
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 mb-4">
              💡 سجِّل كل عملية شراء كسطر منفصل (لا يتم دمج/متوسط). زر <strong>استيراد Excel</strong> يقبل ملف تصدير <strong>البرنامج المحاسبي (22 عمود)</strong> ويربط <em>رقم الصنف المحاسبي</em> بكود <em>المادة الأساسية</em> تلقائياً عبر جدول <strong>أصناف المحاسبة</strong>. يتم تجاهل الصفوف المكررة (بحسب رقم الفاتورة + الصنف + التاريخ).
            </div>

            {/* Filter bar */}
            <div className="bg-gray-50 border rounded-lg p-3 mb-4 flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-gray-600">فلترة بالمورد</Label>
                <select
                  value={filterSupplier}
                  onChange={(e) => setFilterSupplier(e.target.value)}
                  data-testid="filter-supplier"
                  className="w-full h-9 rounded-md border px-2 text-sm bg-white"
                >
                  <option value="">— الكل ({availableSuppliers.length}) —</option>
                  {availableSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-gray-600">فلترة بالتصنيف</Label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  data-testid="filter-category"
                  className="w-full h-9 rounded-md border px-2 text-sm bg-white"
                >
                  <option value="">— الكل ({availableCategories.length}) —</option>
                  {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {(filterSupplier || filterCategory) && (
                <Button onClick={() => { setFilterSupplier(''); setFilterCategory(''); }} size="sm" variant="outline" data-testid="clear-filters-btn">
                  <X className="w-4 h-4 ml-1" /> مسح الفلاتر
                </Button>
              )}
              <div className="text-xs text-gray-600 ml-auto">
                <span className="font-bold text-emerald-700">{filteredPurchases.length}</span> من {purchases.length}
              </div>
            </div>
            {showAddPurchase && (
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 mb-4">
                <h3 className="font-bold text-emerald-900 mb-3">عملية شراء جديدة</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">الكود (من المواد الأساسية) *</Label>
                    <select
                      value={newPurchase.material_code}
                      onChange={(e) => setNewPurchase(p => ({ ...p, material_code: e.target.value }))}
                      className="w-full h-9 rounded-md border px-2 text-sm bg-white"
                      data-testid="new-purchase-code"
                    >
                      <option value="">اختر مادة...</option>
                      {materials.map(m => (
                        <option key={m.id} value={m.code}>{m.code} - {m.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">الكمية *</Label>
                    <Input type="number" step="0.01" value={newPurchase.quantity || ''} onChange={(e) => setNewPurchase(p => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-xs">سعر الوحدة</Label>
                    <Input type="number" step="0.01" value={newPurchase.unit_price || ''} onChange={(e) => setNewPurchase(p => ({ ...p, unit_price: parseFloat(e.target.value) || 0 }))} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-xs">المورد</Label>
                    <Input value={newPurchase.supplier} onChange={(e) => setNewPurchase(p => ({ ...p, supplier: e.target.value }))} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-xs">تاريخ الشراء</Label>
                    <Input type="date" value={newPurchase.purchase_date} onChange={(e) => setNewPurchase(p => ({ ...p, purchase_date: e.target.value }))} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-xs">رقم الفاتورة</Label>
                    <Input value={newPurchase.invoice_number} onChange={(e) => setNewPurchase(p => ({ ...p, invoice_number: e.target.value }))} className="h-9" />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button onClick={handleAddPurchase} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-purchase-btn">حفظ</Button>
                  <Button onClick={() => setShowAddPurchase(false)} size="sm" variant="outline">إلغاء</Button>
                </div>
              </div>
            )}
            {purchases.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="font-semibold">لا توجد مشتريات مسجَّلة</p>
                <p className="text-sm">أضف شراء جديد أو استورد ملف Excel</p>
              </div>
            ) : filteredPurchases.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="font-semibold">لا توجد نتائج للفلاتر الحالية</p>
                <p className="text-sm">جرب فلاتر مختلفة أو امسحها</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-emerald-100">
                    <tr>
                      <th className="p-2 text-right">الكود</th>
                      <th className="p-2 text-right">المادة</th>
                      <th className="p-2 text-center">الكمية</th>
                      <th className="p-2 text-center">الوحدة</th>
                      <th className="p-2 text-center">سعر الوحدة</th>
                      <th className="p-2 text-center">الإجمالي</th>
                      <th className="p-2 text-right">المورد</th>
                      <th className="p-2 text-right">التاريخ</th>
                      <th className="p-2 text-right">الفاتورة</th>
                      <th className="p-2 text-center w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPurchases.map(p => (
                      <tr key={p.id} className="border-b hover:bg-gray-50">
                        <td className="p-2"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-mono text-xs">{p.material_code}</span></td>
                        <td className="p-2">{p.material_name}</td>
                        <td className="p-2 text-center font-bold">{p.quantity}</td>
                        <td className="p-2 text-center text-gray-500">{p.unit}</td>
                        <td className="p-2 text-center">{(p.unit_price || 0).toLocaleString()}</td>
                        <td className="p-2 text-center font-bold text-emerald-700">{((p.quantity || 0) * (p.unit_price || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="p-2">{p.supplier || '—'}</td>
                        <td className="p-2 text-xs">{p.purchase_date || '—'}</td>
                        <td className="p-2 text-xs">{p.invoice_number || '—'}</td>
                        <td className="p-2 text-center">
                          <Button onClick={() => handleDeletePurchase(p.id)} size="sm" variant="ghost" className="h-7 px-2 text-red-600" data-testid={`delete-purchase-${p.id}`}><X className="w-4 h-4" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-emerald-50 font-bold">
                      <td colSpan="5" className="p-2 text-left">إجمالي المشتريات{filterSupplier || filterCategory ? ' (مفلتر)' : ''}:</td>
                      <td className="p-2 text-center text-emerald-700" data-testid="purchases-total">
                        {filteredPurchases.reduce((s, x) => s + (x.quantity || 0) * (x.unit_price || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td colSpan="4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Result Modal (نتيجة استيراد المشتريات) */}
      {importResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="import-result-modal">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b bg-gradient-to-l from-emerald-50 to-blue-50 flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                نتيجة استيراد المشتريات الفعلية
              </h3>
              <button onClick={() => setImportResult(null)} className="text-gray-500 hover:text-red-600" data-testid="close-import-result">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Summary Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700" data-testid="stat-imported">{importResult.imported_count || 0}</div>
                  <div className="text-xs text-emerald-900 mt-1">تم استيراده</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-gray-600" data-testid="stat-duplicates">{importResult.duplicates_skipped || 0}</div>
                  <div className="text-xs text-gray-700 mt-1">مكرر (تم تخطيه)</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700" data-testid="stat-unlinked">{importResult.unlinked_count || 0}</div>
                  <div className="text-xs text-amber-900 mt-1">أصناف غير مربوطة</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-red-700" data-testid="stat-missing">{(importResult.not_in_accounting_count || 0) + (importResult.material_missing_count || 0)}</div>
                  <div className="text-xs text-red-900 mt-1">غير موجود بالمحاسبة</div>
                </div>
              </div>

              {/* Unlinked items list */}
              {importResult.unlinked_items?.length > 0 && (
                <div className="border rounded">
                  <div className="bg-amber-50 border-b border-amber-200 p-2 font-semibold text-sm text-amber-900 flex items-center justify-between">
                    <span>⚠️ أصناف موجودة في جدول المحاسبة لكنها غير مربوطة بمادة أساسية ({importResult.unlinked_count})</span>
                    <button
                      onClick={() => navigate('/buildings/accounting-items')}
                      className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded"
                      data-testid="goto-linking-btn"
                    >
                      اذهب لصفحة الربط
                    </button>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-right">رقم الصنف</th>
                          <th className="p-2 text-right">اسم الصنف</th>
                          <th className="p-2 text-center">تكرار في الملف</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.unlinked_items.map((it, i) => (
                          <tr key={i} className="border-t hover:bg-amber-50/40">
                            <td className="p-2 font-mono">{it.item_number}</td>
                            <td className="p-2">{it.item_name}</td>
                            <td className="p-2 text-center">{it.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Not in accounting list */}
              {importResult.not_in_accounting?.length > 0 && (
                <div className="border rounded">
                  <div className="bg-red-50 border-b border-red-200 p-2 font-semibold text-sm text-red-900">
                    ❌ أرقام أصناف غير موجودة في جدول أصناف المحاسبة ({importResult.not_in_accounting_count})
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-right">صف</th>
                          <th className="p-2 text-right">رقم الصنف</th>
                          <th className="p-2 text-right">اسم الصنف</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.not_in_accounting.map((it, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 text-gray-500">{it.row}</td>
                            <td className="p-2 font-mono">{it.item_number}</td>
                            <td className="p-2">{it.item_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Material Missing list */}
              {importResult.material_missing?.length > 0 && (
                <div className="border rounded">
                  <div className="bg-orange-50 border-b border-orange-200 p-2 font-semibold text-sm text-orange-900">
                    ⚠️ الكود المربوط غير موجود في المواد الأساسية ({importResult.material_missing_count})
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-right">رقم الصنف (محاسبي)</th>
                          <th className="p-2 text-right">الكود المربوط</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.material_missing.map((it, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-mono">{it.item_number}</td>
                            <td className="p-2 font-mono">{it.linked_code}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setImportResult(null)} data-testid="import-result-close-btn">
                إغلاق
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Unmapped Imports Tab (مشتريات غير مخططة) */}
      {activeTab === 'unplanned' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gradient-to-l from-amber-50 to-orange-50 p-4 flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              مشتريات غير مخططة
              {unmapped?.totals?.rows_count > 0 && (
                <span className="text-sm bg-amber-600 text-white px-2 py-0.5 rounded-full">
                  {unmapped.totals.rows_count}
                </span>
              )}
              {unmapped?.imported_at && (
                <span className="text-xs text-gray-500 font-normal ml-2">
                  آخر استيراد: {new Date(unmapped.imported_at).toLocaleString('ar-EG')}
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={loadUnmapped} size="sm" variant="outline" data-testid="load-unmapped-btn">
                <RefreshCw className="w-4 h-4 ml-1" /> تحميل/تحديث
              </Button>
              <Button onClick={handleExportUnmappedExcel} size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!unmapped?.rows?.length} data-testid="export-unmapped-excel">
                <Download className="w-4 h-4 ml-1" /> Excel
              </Button>
              <Button onClick={handleExportUnmappedPdf} size="sm" className="bg-red-600 hover:bg-red-700" disabled={!unmapped?.rows?.length} data-testid="export-unmapped-pdf">
                <FileText className="w-4 h-4 ml-1" /> PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Filter Bar */}
            <div className="bg-gray-50 border rounded-lg p-3 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs text-gray-600">بحث (رقم/اسم الصنف)</Label>
                  <Input
                    value={unmappedFilters.q}
                    onChange={(e) => setUnmappedFilters({ ...unmappedFilters, q: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') loadUnmapped(); }}
                    data-testid="filter-q"
                    className="h-9 text-sm"
                    placeholder="اكتب للبحث..."
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">المورد</Label>
                  <select
                    value={unmappedFilters.supplier}
                    onChange={(e) => setUnmappedFilters({ ...unmappedFilters, supplier: e.target.value })}
                    data-testid="filter-supplier-unmapped"
                    className="w-full h-9 rounded-md border px-2 text-sm bg-white"
                  >
                    <option value="">— الكل ({unmappedFilterOptions.suppliers.length}) —</option>
                    {unmappedFilterOptions.suppliers.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">الوحدة</Label>
                  <select
                    value={unmappedFilters.unit}
                    onChange={(e) => setUnmappedFilters({ ...unmappedFilters, unit: e.target.value })}
                    data-testid="filter-unit-unmapped"
                    className="w-full h-9 rounded-md border px-2 text-sm bg-white"
                  >
                    <option value="">— الكل —</option>
                    {unmappedFilterOptions.units.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <div>
                    <Label className="text-xs text-gray-600">من تاريخ</Label>
                    <Input
                      type="date"
                      value={unmappedFilters.date_from}
                      onChange={(e) => setUnmappedFilters({ ...unmappedFilters, date_from: e.target.value })}
                      data-testid="filter-date-from"
                      className="h-9 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">إلى تاريخ</Label>
                    <Input
                      type="date"
                      value={unmappedFilters.date_to}
                      onChange={(e) => setUnmappedFilters({ ...unmappedFilters, date_to: e.target.value })}
                      data-testid="filter-date-to"
                      className="h-9 text-xs"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">الحد الأدنى للكمية</Label>
                  <Input
                    type="number"
                    value={unmappedFilters.min_qty}
                    onChange={(e) => setUnmappedFilters({ ...unmappedFilters, min_qty: e.target.value })}
                    data-testid="filter-min-qty"
                    className="h-9 text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">الحد الأدنى للتكلفة</Label>
                  <Input
                    type="number"
                    value={unmappedFilters.min_cost}
                    onChange={(e) => setUnmappedFilters({ ...unmappedFilters, min_cost: e.target.value })}
                    data-testid="filter-min-cost"
                    className="h-9 text-sm"
                    placeholder="0"
                  />
                </div>
                <div className="flex items-end gap-2 md:col-span-2">
                  <Button size="sm" onClick={loadUnmapped} className="bg-blue-600 hover:bg-blue-700 flex-1" data-testid="apply-unmapped-filters">
                    تطبيق الفلاتر
                  </Button>
                  <Button size="sm" variant="outline" onClick={resetUnmappedFilters} data-testid="reset-unmapped-filters">
                    مسح الفلاتر
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-4">
              ⚠️ هذه الأصناف موجودة في ملف Excel المستورد للمشتريات، لكنها <strong>غير مربوطة</strong> بمادة أساسية في جدول أصناف المحاسبة، لذلك لم يُدخلها النظام في المشتريات الفعلية. اضغط <strong>«إضافة للمبدئية»</strong> لإنشاء مادة أساسية جديدة تلقائياً وربطها وإضافتها ككمية مبدئية للمشروع.
            </div>

            {unmappedLoading ? (
              <div className="text-center py-10 text-gray-500" data-testid="unmapped-loading">جاري التحميل...</div>
            ) : !unmapped ? (
              <div className="text-center py-10 text-gray-500">
                اضغط <strong>«تحميل/تحديث»</strong> لعرض البيانات
              </div>
            ) : unmapped.rows.length === 0 ? (
              <div className="text-center py-10 text-emerald-600 font-semibold" data-testid="unmapped-empty">
                ✓ لا توجد أصناف غير مربوطة — كل الأصناف في آخر ملف مستورد مربوطة أو تم التعامل معها.
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center">
                    <div className="text-xs text-amber-800">عدد الأصناف</div>
                    <div className="text-2xl font-bold text-amber-700" data-testid="unmapped-rows-count">{unmapped.totals.rows_count}</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded p-3 text-center">
                    <div className="text-xs text-orange-800">إجمالي عمليات الشراء</div>
                    <div className="text-2xl font-bold text-orange-700" data-testid="unmapped-purchases-count">{unmapped.totals.purchases_count}</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                    <div className="text-xs text-red-800">إجمالي الكمية</div>
                    <div className="text-lg font-bold text-red-700">{(unmapped.totals.total_quantity || 0).toLocaleString('ar-EG', { maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className="bg-rose-50 border border-rose-200 rounded p-3 text-center">
                    <div className="text-xs text-rose-800">التكلفة الإجمالية</div>
                    <div className="text-lg font-bold text-rose-700" data-testid="unmapped-total-cost">{(unmapped.totals.total_cost || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} ر.س</div>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-xs">
                      <tr>
                        <th className="p-2 text-right border-b">رقم الصنف</th>
                        <th className="p-2 text-right border-b">اسم الصنف</th>
                        <th className="p-2 text-center border-b">الوحدة</th>
                        <th className="p-2 text-center border-b">الكمية</th>
                        <th className="p-2 text-center border-b">التكلفة</th>
                        <th className="p-2 text-center border-b">متوسط السعر</th>
                        <th className="p-2 text-right border-b">تفاصيل المشتريات</th>
                        <th className="p-2 text-center border-b">إجراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmapped.rows.map((r, i) => {
                        const avgPrice = r.total_quantity > 0 ? (r.total_cost / r.total_quantity) : 0;
                        const itemNum = String(r.item_number);
                        return (
                          <tr key={itemNum} className="border-b hover:bg-amber-50/40" data-testid={`unmapped-row-${i}`}>
                            <td className="p-2 font-mono text-xs">{itemNum}</td>
                            <td className="p-2 font-semibold">{r.item_name}</td>
                            <td className="p-2 text-center text-gray-600">{r.unit || '—'}</td>
                            <td className="p-2 text-center font-bold text-amber-700">
                              {(r.total_quantity || 0).toLocaleString('ar-EG', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="p-2 text-center font-bold text-rose-700">
                              {(r.total_cost || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 })}
                            </td>
                            <td className="p-2 text-center text-gray-700">
                              {avgPrice.toLocaleString('ar-EG', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="p-2 text-xs text-gray-600 min-w-[220px]">
                              {(!r.purchases || r.purchases.length === 0) ? (
                                <span className="text-gray-400">—</span>
                              ) : (
                                <div className="max-h-24 overflow-y-auto space-y-0.5">
                                  {r.purchases.slice(0, 5).map((p, pi) => (
                                    <div key={pi} className="flex items-center gap-1">
                                      <span className="font-semibold text-blue-700">{p.quantity}</span>
                                      <span>×</span>
                                      <span className="font-semibold text-emerald-700">{p.unit_price}</span>
                                      <span className="text-gray-500 text-[10px]">·</span>
                                      <span className="text-gray-700 truncate max-w-[100px]" title={p.supplier}>{p.supplier || '—'}</span>
                                      {p.invoice_number && (
                                        <span className="text-gray-400 text-[10px]">#{p.invoice_number}</span>
                                      )}
                                    </div>
                                  ))}
                                  {r.purchases.length > 5 && (
                                    <div className="text-[10px] text-gray-400">+{r.purchases.length - 5} أخرى...</div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex gap-1 justify-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-xs"
                                  onClick={() => handleAddUnmappedToPlanned(itemNum, r.item_name)}
                                  disabled={addingToPlanned === itemNum}
                                  data-testid={`add-unmapped-${itemNum}`}
                                >
                                  {addingToPlanned === itemNum ? '...' : (<><Plus className="w-3 h-3 ml-1" /> إضافة للمبدئية</>)}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:bg-red-50 text-xs"
                                  onClick={() => handleDismissUnmapped(itemNum, r.item_name)}
                                  data-testid={`dismiss-unmapped-${itemNum}`}
                                  title="تجاهل هذا الصنف"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add-Unmapped-to-Planned Modal (اختيار: مادة موجودة أو جديدة) */}
      {addUnmappedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-unmapped-modal">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b bg-gradient-to-l from-emerald-50 to-blue-50 flex items-center justify-between">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" />
                إضافة صنف للكمية المبدئية
              </h3>
              <button onClick={() => setAddUnmappedModal(null)} className="text-gray-500 hover:text-red-600" data-testid="close-add-unmapped-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* معلومات الصنف المحاسبي */}
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-600">رقم الصنف المحاسبي:</span> <strong className="font-mono">{addUnmappedModal.itemNumber}</strong></div>
                  <div><span className="text-gray-600">الاسم في المحاسبة:</span> <strong>{addUnmappedModal.itemName}</strong></div>
                  <div><span className="text-gray-600">الكمية الإجمالية:</span> <strong className="text-amber-700">{addUnmappedModal.totalQty}</strong> {addUnmappedModal.unit}</div>
                  <div><span className="text-gray-600">التكلفة الإجمالية:</span> <strong className="text-rose-700">{Number(addUnmappedModal.totalCost).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} ر.س</strong></div>
                </div>
              </div>

              {/* اختيار الوضع */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">اختر طريقة الإضافة:</Label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 border rounded p-2 cursor-pointer hover:bg-gray-50" data-testid="mode-create-new">
                    <input
                      type="radio"
                      name="add-mode"
                      value="create_new"
                      checked={addUnmappedModal.mode === 'create_new'}
                      onChange={() => setAddUnmappedModal({ ...addUnmappedModal, mode: 'create_new' })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-emerald-700">إنشاء مادة جديدة</div>
                      <div className="text-xs text-gray-600">تُنشأ مادة جديدة في قاعدة الأصناف باسم وكود جديدين.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 border rounded p-2 cursor-pointer hover:bg-gray-50" data-testid="mode-link-existing">
                    <input
                      type="radio"
                      name="add-mode"
                      value="link_existing"
                      checked={addUnmappedModal.mode === 'link_existing'}
                      onChange={() => setAddUnmappedModal({ ...addUnmappedModal, mode: 'link_existing' })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-blue-700">ربط بمادة موجودة</div>
                      <div className="text-xs text-gray-600">اختر مادة موجودة في قاعدة الأصناف لربط هذا الصنف بها.</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* حقول وضع create_new */}
              {addUnmappedModal.mode === 'create_new' && (
                <div className="space-y-2 border-t pt-3">
                  <div>
                    <Label className="text-xs text-gray-700">كود المادة الجديد</Label>
                    <Input
                      value={addUnmappedModal.newMaterialCode}
                      onChange={(e) => setAddUnmappedModal({ ...addUnmappedModal, newMaterialCode: e.target.value })}
                      data-testid="new-mat-code"
                      className="text-sm"
                      placeholder="مثال: BLK-40"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-700">اسم المادة</Label>
                    <Input
                      value={addUnmappedModal.newMaterialName}
                      onChange={(e) => setAddUnmappedModal({ ...addUnmappedModal, newMaterialName: e.target.value })}
                      data-testid="new-mat-name"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-700">الوحدة</Label>
                    <Input
                      value={addUnmappedModal.newMaterialUnit}
                      onChange={(e) => setAddUnmappedModal({ ...addUnmappedModal, newMaterialUnit: e.target.value })}
                      data-testid="new-mat-unit"
                      className="text-sm"
                      placeholder="مثال: حبة، طن، كيس"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-700">التصنيف (اختياري)</Label>
                    <select
                      value={addUnmappedModal.newMaterialCategory}
                      onChange={(e) => setAddUnmappedModal({ ...addUnmappedModal, newMaterialCategory: e.target.value })}
                      data-testid="new-mat-category"
                      className="w-full h-9 rounded-md border px-2 text-sm bg-white"
                    >
                      <option value="">— بدون تصنيف —</option>
                      {categories.map(c => (
                        <option key={c.id || c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                    {categories.length === 0 && (
                      <div className="text-[10px] text-gray-500 mt-1">لا توجد تصنيفات محفوظة. يمكنك إضافتها من «المواد الأساسية».</div>
                    )}
                  </div>
                </div>
              )}

              {/* حقول وضع link_existing */}
              {addUnmappedModal.mode === 'link_existing' && (
                <div className="space-y-2 border-t pt-3">
                  <div>
                    <Label className="text-xs text-gray-700">ابحث في المواد الأساسية (بالكود أو الاسم)</Label>
                    <Input
                      value={addUnmappedModal.linkSearch}
                      onChange={(e) => setAddUnmappedModal({ ...addUnmappedModal, linkSearch: e.target.value })}
                      data-testid="link-search"
                      className="text-sm"
                      placeholder="اكتب للبحث..."
                      autoFocus
                    />
                  </div>
                  <div className="border rounded max-h-52 overflow-y-auto" data-testid="link-results">
                    {materials
                      .filter(m => {
                        const s = (addUnmappedModal.linkSearch || '').trim().toLowerCase();
                        if (!s) return true;
                        return (m.code || '').toLowerCase().includes(s) || (m.name || '').toLowerCase().includes(s);
                      })
                      .slice(0, 50)
                      .map(m => (
                        <label
                          key={m.code}
                          className={`flex items-center gap-2 p-2 border-b cursor-pointer hover:bg-blue-50 ${addUnmappedModal.linkMaterialCode === m.code ? 'bg-blue-100' : ''}`}
                          data-testid={`link-option-${m.code}`}
                        >
                          <input
                            type="radio"
                            name="link-material"
                            value={m.code}
                            checked={addUnmappedModal.linkMaterialCode === m.code}
                            onChange={() => setAddUnmappedModal({ ...addUnmappedModal, linkMaterialCode: m.code })}
                          />
                          <div className="flex-1 text-xs">
                            <div className="font-mono text-gray-700">{m.code}</div>
                            <div className="font-semibold">{m.name}</div>
                            <div className="text-gray-500">الوحدة: {m.unit || '—'}</div>
                          </div>
                        </label>
                      ))}
                    {materials.filter(m => {
                      const s = (addUnmappedModal.linkSearch || '').trim().toLowerCase();
                      if (!s) return true;
                      return (m.code || '').toLowerCase().includes(s) || (m.name || '').toLowerCase().includes(s);
                    }).length === 0 && (
                      <div className="text-center text-gray-400 py-4 text-xs">لا توجد نتائج</div>
                    )}
                  </div>
                  {addUnmappedModal.linkMaterialCode && (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                      ✓ سيتم ربط الصنف المحاسبي رقم <strong>{addUnmappedModal.itemNumber}</strong> بالكود <strong className="font-mono">{addUnmappedModal.linkMaterialCode}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-3 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddUnmappedModal(null)} data-testid="cancel-add-unmapped">
                إلغاء
              </Button>
              <Button
                onClick={confirmAddUnmapped}
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={addingToPlanned === addUnmappedModal.itemNumber}
                data-testid="confirm-add-unmapped"
              >
                {addingToPlanned === addUnmappedModal.itemNumber ? 'جاري الإضافة...' : 'تأكيد الإضافة'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Tab (الجدول الزمني) */}
      {activeTab === 'timeline' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gradient-to-l from-purple-50 to-indigo-50 p-4 flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              الجدول الزمني للمشروع
              {timeline?.totals?.total > 0 && (
                <span className="text-sm bg-purple-600 text-white px-2 py-0.5 rounded-full">
                  {timeline.totals.total} نشاط
                </span>
              )}
              {timeline?.file_name && (
                <span className="text-xs text-gray-500 font-normal ml-2">📎 {timeline.file_name}</span>
              )}
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <input
                ref={timelineFileRef}
                type="file"
                accept=".xlsx,.xls,.pdf"
                onChange={handleImportTimeline}
                className="hidden"
                data-testid="timeline-file-input"
              />
              <Button onClick={() => timelineFileRef.current?.click()} size="sm" className="bg-purple-600 hover:bg-purple-700" data-testid="upload-timeline-btn">
                <Upload className="w-4 h-4 ml-1" /> رفع Excel/PDF
              </Button>
              <Button onClick={loadTimeline} size="sm" variant="outline" data-testid="load-timeline-btn">
                <RefreshCw className="w-4 h-4 ml-1" /> تحديث
              </Button>
              {timeline?.totals?.total > 0 && (
                <Button onClick={handleClearTimeline} size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-timeline-btn">
                  <Trash2 className="w-4 h-4 ml-1" /> حذف
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {timelineLoading ? (
              <div className="text-center py-10 text-gray-500">جاري التحميل...</div>
            ) : !timeline || timeline.totals?.total === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-semibold mb-2">لم يتم رفع جدول زمني بعد</p>
                <p className="text-xs text-gray-500 mb-4">
                  يقبل النظام ملفات <strong>Excel</strong> بصيغتين + ملفات <strong>PDF</strong> (Primavera export).<br/>
                  <span className="text-purple-700">اكتشاف الأعمدة تلقائي — يكفي أن يحتوي الملف على العناوين الصحيحة.</span><br/>
                  <span className="text-amber-700">⚠️ استخراج PDF تقريبي — يُفضَّل Excel للدقة الكاملة.</span>
                </p>
                <Button onClick={() => timelineFileRef.current?.click()} className="bg-purple-600 hover:bg-purple-700" data-testid="upload-timeline-empty-btn">
                  <Upload className="w-4 h-4 ml-1" /> رفع الجدول الزمني
                </Button>
              </div>
            ) : (
              <>
                {/* Reminder Banner — الأنشطة القادمة خلال 14 يوم */}
                {timeline.totals.upcoming_soon_14d > 0 && (
                  <div className="bg-gradient-to-l from-amber-50 to-orange-50 border-r-4 border-amber-500 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-bold text-amber-900 mb-1" data-testid="reminder-title">
                          🔔 تذكير: {timeline.totals.upcoming_soon_14d} نشاط يبدأ خلال 14 يوماً
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                          {timeline.items
                            .filter(i => i.status === 'قادم' && (i.days_until_start ?? 999) <= 14 && i.row_type === 'نشاط')
                            .slice(0, 6)
                            .map(i => (
                              <div key={i.activity_id} className="bg-white/70 border border-amber-200 rounded p-2 text-xs">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-semibold text-amber-900 truncate">{i.activity_name}</span>
                                  <span className="bg-amber-600 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">
                                    بعد {i.days_until_start} يوم
                                  </span>
                                </div>
                                <div className="text-gray-600 mt-1 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{i.start_date}</span>
                                  <span className="text-gray-400">→</span>
                                  <span>{i.end_date}</span>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
                    <div className="text-xs text-blue-800">الإجمالي</div>
                    <div className="text-2xl font-bold text-blue-700" data-testid="timeline-total">{timeline.totals.total}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center cursor-pointer" onClick={() => setTimelineFilter('upcoming')}>
                    <div className="text-xs text-amber-800">قادم</div>
                    <div className="text-2xl font-bold text-amber-700" data-testid="timeline-upcoming">{timeline.totals.upcoming}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center cursor-pointer" onClick={() => setTimelineFilter('in_progress')}>
                    <div className="text-xs text-emerald-800">جارٍ التنفيذ</div>
                    <div className="text-2xl font-bold text-emerald-700" data-testid="timeline-in-progress">{timeline.totals.in_progress}</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 text-center cursor-pointer" onClick={() => setTimelineFilter('completed')}>
                    <div className="text-xs text-gray-700">مكتمل</div>
                    <div className="text-2xl font-bold text-gray-700" data-testid="timeline-completed">{timeline.totals.completed}</div>
                  </div>
                </div>

                {/* Filter Bar */}
                <div className="flex gap-2 flex-wrap mb-3">
                  {[
                    { id: 'all', label: 'الكل', color: 'bg-slate-600' },
                    { id: 'upcoming', label: 'قادم', color: 'bg-amber-600' },
                    { id: 'in_progress', label: 'جارٍ', color: 'bg-emerald-600' },
                    { id: 'completed', label: 'مكتمل', color: 'bg-gray-600' },
                    { id: 'upcoming_soon', label: 'قريبًا (14 يوم)', color: 'bg-orange-600' },
                  ].map(f => (
                    <button
                      key={f.id}
                      onClick={() => setTimelineFilter(f.id)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition ${timelineFilter === f.id ? `${f.color} text-white` : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      data-testid={`timeline-filter-${f.id}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                {/* Timeline Table */}
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-xs sticky top-0">
                      <tr>
                        <th className="p-2 text-right border-b">رقم النشاط</th>
                        <th className="p-2 text-right border-b">اسم النشاط</th>
                        <th className="p-2 text-center border-b">النوع</th>
                        <th className="p-2 text-center border-b">تاريخ البدء</th>
                        <th className="p-2 text-center border-b">تاريخ الانتهاء</th>
                        <th className="p-2 text-center border-b">المدة</th>
                        <th className="p-2 text-center border-b">الحالة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeline.items
                        .filter(i => {
                          if (timelineFilter === 'all') return true;
                          if (timelineFilter === 'upcoming') return i.status === 'قادم';
                          if (timelineFilter === 'in_progress') return i.status === 'جارٍ';
                          if (timelineFilter === 'completed') return i.status === 'مكتمل';
                          if (timelineFilter === 'upcoming_soon') return i.status === 'قادم' && (i.days_until_start ?? 999) <= 14;
                          return true;
                        })
                        .map((i, idx) => {
                          const isSummary = (i.row_type || '').includes('ملخص');
                          const statusColor = {
                            'قادم': 'bg-amber-100 text-amber-800',
                            'جارٍ': 'bg-emerald-100 text-emerald-800',
                            'مكتمل': 'bg-gray-100 text-gray-600',
                          }[i.status] || 'bg-slate-100 text-slate-600';
                          return (
                            <tr key={idx} className={`border-b hover:bg-purple-50/30 ${isSummary ? 'bg-indigo-50/50 font-semibold' : ''}`} data-testid={`timeline-row-${idx}`}>
                              <td className="p-2 font-mono text-xs">{i.activity_id}</td>
                              <td className="p-2">{i.activity_name}</td>
                              <td className="p-2 text-center text-xs">
                                {isSummary ? (
                                  <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-[10px]">{i.row_type}</span>
                                ) : (
                                  <span className="text-gray-500 text-[10px]">نشاط</span>
                                )}
                              </td>
                              <td className="p-2 text-center text-xs">{i.start_date || '—'}</td>
                              <td className="p-2 text-center text-xs">{i.end_date || '—'}</td>
                              <td className="p-2 text-center text-xs">
                                {i.duration_days ? `${i.duration_days} يوم` : '—'}
                              </td>
                              <td className="p-2 text-center">
                                <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${statusColor}`}>
                                  {i.status}
                                </span>
                                {i.status === 'قادم' && i.days_until_start !== null && (
                                  <div className="text-[10px] text-gray-500 mt-0.5">بعد {i.days_until_start} يوم</div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comparison Report Tab (تقرير المقارنة) */}
      {activeTab === 'comparison' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gradient-to-l from-purple-50 to-pink-50 p-4 flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Weight className="w-5 h-5 text-purple-600" />
              تقرير مقارنة الكميات المبدئية × الفعلية
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={loadComparison} size="sm" variant="outline" data-testid="load-comparison-btn">
                <Package className="w-4 h-4 ml-1" /> تحميل/تحديث
              </Button>
              <Button onClick={handleExportComparisonExcel} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-comparison-excel" disabled={!comparison}>
                Excel (الفلتر الحالي)
              </Button>
              <Button onClick={() => handleExportComparisonPdf('current')} size="sm" className="bg-red-600 hover:bg-red-700" data-testid="export-comparison-pdf-current" disabled={!comparison}>
                PDF (الفلتر الحالي)
              </Button>
              {(filterSupplier || filterCategory) && (
                <Button onClick={() => handleExportComparisonPdf('all')} size="sm" variant="outline" className="border-red-400 text-red-600" data-testid="export-comparison-pdf-all" disabled={!comparison}>
                  PDF (الكل)
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Filter bar for comparison */}
            <div className="bg-purple-50/50 border border-purple-200 rounded-lg p-3 mb-4 flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-purple-700">المورد</Label>
                <select value={filterSupplier} onChange={(e) => setFilterSupplier(e.target.value)} data-testid="cmp-filter-supplier" className="w-full h-9 rounded-md border px-2 text-sm bg-white">
                  <option value="">— الكل —</option>
                  {availableSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-purple-700">التصنيف</Label>
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} data-testid="cmp-filter-category" className="w-full h-9 rounded-md border px-2 text-sm bg-white">
                  <option value="">— الكل —</option>
                  {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {(filterSupplier || filterCategory) && (
                <Button onClick={() => { setFilterSupplier(''); setFilterCategory(''); }} size="sm" variant="outline" data-testid="cmp-clear-filters"><X className="w-4 h-4 ml-1" /> مسح</Button>
              )}
              <div className="text-[11px] text-gray-600 ml-auto bg-white px-2 py-1 rounded border">
                💡 اضغط <strong>«تحميل/تحديث»</strong> بعد تغيير الفلاتر
              </div>
            </div>
            {!comparison ? (
              <div className="text-center py-12 text-gray-400">
                <Weight className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="font-semibold">اضغط «تحميل/تحديث» لإنشاء التقرير</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                    <div className="text-xs text-blue-700">عدد الأصناف</div>
                    <div className="text-xl font-bold text-blue-900">{comparison.totals.rows_count}</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
                    <div className="text-xs text-purple-700">تكلفة مبدئية</div>
                    <div className="text-base font-bold text-purple-900">{comparison.totals.planned_cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                    <div className="text-xs text-emerald-700">تكلفة فعلية</div>
                    <div className="text-base font-bold text-emerald-900">{comparison.totals.actual_cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className={`border rounded-xl p-3 text-center ${comparison.totals.cost_diff > 0 ? 'bg-red-50 border-red-200' : comparison.totals.cost_diff < 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className={`text-xs ${comparison.totals.cost_diff > 0 ? 'text-red-700' : comparison.totals.cost_diff < 0 ? 'text-green-700' : 'text-gray-700'}`}>الفرق</div>
                    <div className={`text-base font-bold ${comparison.totals.cost_diff > 0 ? 'text-red-900' : comparison.totals.cost_diff < 0 ? 'text-green-900' : 'text-gray-900'}`}>
                      {comparison.totals.cost_diff > 0 ? '+' : ''}{comparison.totals.cost_diff.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                {/* Comparison Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-purple-100">
                      <tr>
                        <th className="p-2 text-right">الكود</th>
                        <th className="p-2 text-right">الصنف</th>
                        <th className="p-2 text-center">الوحدة</th>
                        <th className="p-2 text-center bg-purple-50">كمية مبدئية</th>
                        <th className="p-2 text-center bg-purple-50">تكلفة مبدئية</th>
                        <th className="p-2 text-center bg-emerald-50">كمية فعلية</th>
                        <th className="p-2 text-center bg-emerald-50">تكلفة فعلية</th>
                        <th className="p-2 text-center">فرق كمية</th>
                        <th className="p-2 text-center">فرق تكلفة</th>
                        <th className="p-2 text-center">تفاصيل الشراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.rows.map((r, idx) => (
                        <tr key={idx} className={`border-b align-top ${r.is_unplanned ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`} data-testid={`comparison-row-${idx}`}>
                          <td className="p-2"><span className="px-2 py-0.5 bg-gray-100 rounded font-mono text-xs">{r.code}</span></td>
                          <td className="p-2 font-medium">
                            {r.is_unplanned && (
                              <span className="inline-block bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded ml-1 align-middle" title="صنف مربوط بأصناف المحاسبة لكن ليس له كمية مبدئية">
                                ⚠ غير مخطط
                              </span>
                            )}
                            {r.name}
                          </td>
                          <td className="p-2 text-center text-gray-500">{r.unit}</td>
                          <td className="p-2 text-center bg-purple-50/30 font-bold">{r.planned_qty.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="p-2 text-center bg-purple-50/30">{r.planned_cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="p-2 text-center bg-emerald-50/30 font-bold">{r.actual_qty.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="p-2 text-center bg-emerald-50/30">{r.actual_cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className={`p-2 text-center font-bold ${r.qty_diff > 0 ? 'text-red-700' : r.qty_diff < 0 ? 'text-green-700' : ''}`}>
                            {r.qty_diff > 0 ? '+' : ''}{r.qty_diff.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            {r.qty_diff_pct != null && <div className="text-[10px] font-normal">({r.qty_diff_pct.toFixed(1)}%)</div>}
                          </td>
                          <td className={`p-2 text-center font-bold ${r.cost_diff > 0 ? 'text-red-700' : r.cost_diff < 0 ? 'text-green-700' : ''}`}>
                            {r.cost_diff > 0 ? '+' : ''}{r.cost_diff.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            {r.cost_diff_pct != null && <div className="text-[10px] font-normal">({r.cost_diff_pct.toFixed(1)}%)</div>}
                          </td>
                          <td className="p-2 text-xs">
                            {r.purchases.length === 0 ? <span className="text-gray-400">لا توجد</span> : (
                              <ul className="space-y-0.5">
                                {r.purchases.map((p, i) => (
                                  <li key={i} className="text-gray-600">
                                    <span className="font-bold">{p.quantity}</span> × {p.unit_price.toLocaleString()} = <span className="text-emerald-700 font-semibold">{p.line_total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                                    {p.supplier && <span className="text-gray-500"> • {p.supplier}</span>}
                                    {p.invoice_number && <span className="text-gray-400"> • #{p.invoice_number}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}



      {/* BOQ Tab */}
      {activeTab === 'boq' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gray-50 p-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              جدول الكميات (BOQ)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {/* Unit Materials */}
            {calculation?.materials?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-600 mb-2 flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  مواد الشقق
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-3 text-right">الكود</th>
                        <th className="p-3 text-right">المادة</th>
                        <th className="p-3 text-center">الوحدة</th>
                        <th className="p-3 text-center">الكمية</th>
                        <th className="p-3 text-center">السعر</th>
                        <th className="p-3 text-center">الإجمالي</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculation.materials.map((mat, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-mono text-xs">
                              {mat.material_code}
                            </span>
                          </td>
                          <td className="p-3 font-medium">{mat.material_name}</td>
                          <td className="p-3 text-center text-gray-500">{mat.unit}</td>
                          <td className="p-3 text-center font-bold">{mat.quantity?.toLocaleString()}</td>
                          <td className="p-3 text-center">{mat.unit_price?.toLocaleString()}</td>
                          <td className="p-3 text-center font-bold text-emerald-600">
                            {mat.total_price?.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-blue-50 font-bold">
                        <td colSpan={5} className="p-3 text-left">إجمالي مواد الشقق</td>
                        <td className="p-3 text-center text-blue-600">
                          {calculation.total_unit_materials_cost?.toLocaleString()} ريال
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Area Materials */}
            {calculation?.area_materials?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-600 mb-2 flex items-center gap-2">
                  <Weight className="w-4 h-4" />
                  مواد المساحة (حديد، بلك، إلخ)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-purple-100">
                        <th className="p-3 text-right">الكود</th>
                        <th className="p-3 text-right">المادة</th>
                        <th className="p-3 text-center">المعامل</th>
                        <th className="p-3 text-center">الوحدة</th>
                        <th className="p-3 text-center">الكمية</th>
                        <th className="p-3 text-center">السعر</th>
                        <th className="p-3 text-center">الإجمالي</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculation.area_materials.map((mat, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-mono text-xs">
                              {mat.material_code}
                            </span>
                          </td>
                          <td className="p-3 font-medium">{mat.material_name}</td>
                          <td className="p-3 text-center text-purple-600 font-bold">{mat.factor}</td>
                          <td className="p-3 text-center text-gray-500">{mat.unit}</td>
                          <td className="p-3 text-center font-bold">{mat.quantity?.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                          <td className="p-3 text-center">{mat.unit_price?.toLocaleString()}</td>
                          <td className="p-3 text-center font-bold text-emerald-600">
                            {mat.total_price?.toLocaleString(undefined, {maximumFractionDigits: 2})}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-purple-50 font-bold">
                        <td colSpan={6} className="p-3 text-left">إجمالي مواد المساحة</td>
                        <td className="p-3 text-center text-purple-600">
                          {calculation.total_area_materials_cost?.toLocaleString()} ريال
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Grand Total */}
            {(calculation?.materials?.length > 0 || calculation?.area_materials?.length > 0) && (
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 rounded-xl text-center">
                <p className="text-sm opacity-90">الإجمالي الكلي</p>
                <p className="text-3xl font-bold">{calculation.total_materials_cost?.toLocaleString()} ريال</p>
              </div>
            )}

            {(!calculation?.materials?.length && !calculation?.area_materials?.length) && (
              <p className="text-center text-gray-400 py-8">أضف نماذج الشقق ومواد المساحة لحساب الكميات</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Steel Tab */}
      {activeTab === 'steel' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gray-50 p-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Weight className="w-5 h-5 text-amber-600" />
              حساب الحديد
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-500">المساحة الإجمالية</p>
                <p className="text-2xl font-bold text-blue-600">
                  {calculation?.steel_calculation?.total_area?.toLocaleString()} م²
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-500">معامل التسليح</p>
                <p className="text-2xl font-bold text-purple-600">
                  {calculation?.steel_calculation?.steel_factor} كجم/م²
                </p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-500">إجمالي الحديد</p>
                <p className="text-3xl font-bold text-amber-600">
                  {calculation?.steel_calculation?.total_steel_tons?.toFixed(2)} طن
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  ({calculation?.steel_calculation?.total_steel_kg?.toLocaleString()} كجم)
                </p>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>معادلة الحساب:</strong> المساحة × معامل التسليح = إجمالي الحديد
              </p>
              <p className="text-sm text-blue-600 mt-1">
                {calculation?.steel_calculation?.total_area?.toLocaleString()} م² × {calculation?.steel_calculation?.steel_factor} كجم/م² = {calculation?.steel_calculation?.total_steel_kg?.toLocaleString()} كجم
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supply Tracking Tab */}
      {activeTab === 'supply' && (
        <Card className="shadow-md border-0">
          <CardHeader className="bg-gray-50 p-4">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-5 h-5 text-purple-600" />
                تتبع التوريد
                <span className="text-sm font-normal text-gray-500">({supplyStatus.length} صنف)</span>
              </CardTitle>
              <div className="flex gap-2">
                {supplyStatus.length > 0 && (
                  <Button onClick={handleSyncSupply} size="sm" variant="outline" className="text-blue-600 border-blue-300">
                    <RefreshCw className="w-4 h-4 ml-1" />
                    مزامنة المواد
                  </Button>
                )}
                {supplyStatus.length === 0 && (calculation?.materials?.length > 0 || calculation?.area_materials?.length > 0 || buildingMaterials?.length > 0) && (
                  <Button onClick={handleInitSupply} size="sm" className="bg-purple-600 hover:bg-purple-700">
                    تهيئة التتبع
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {supplyStatus.length > 0 ? (
              <>
                {/* إحصائيات سريعة */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-gray-100 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-700">{supplyStats.total}</div>
                    <div className="text-xs text-gray-500">إجمالي الأصناف</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200">
                    <div className="text-2xl font-bold text-red-600">{supplyStats.notReceived}</div>
                    <div className="text-xs text-red-500">غير مستلم</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-200">
                    <div className="text-2xl font-bold text-yellow-600">{supplyStats.partial}</div>
                    <div className="text-xs text-yellow-600">مستلم جزئي</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                    <div className="text-2xl font-bold text-green-600">{supplyStats.complete}</div>
                    <div className="text-xs text-green-500">مكتمل</div>
                  </div>
                </div>

                {/* البحث والفلاتر */}
                <div className="flex flex-wrap gap-3 mb-4 p-3 bg-white border rounded-lg">
                  {/* حقل البحث */}
                  <div className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      placeholder="🔍 بحث بالاسم أو الكود..."
                      value={supplySearch}
                      onChange={(e) => { setSupplySearch(e.target.value); setSupplyPage(1); }}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* فلاتر الحالة */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setSupplyFilter('all'); setSupplyPage(1); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        supplyFilter === 'all' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      الكل ({supplyStats.total})
                    </button>
                    <button
                      onClick={() => { setSupplyFilter('not_received'); setSupplyPage(1); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        supplyFilter === 'not_received' 
                          ? 'bg-red-600 text-white' 
                          : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      }`}
                    >
                      غير مستلم ({supplyStats.notReceived})
                    </button>
                    <button
                      onClick={() => { setSupplyFilter('partial'); setSupplyPage(1); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        supplyFilter === 'partial' 
                          ? 'bg-yellow-500 text-white' 
                          : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100 border border-yellow-200'
                      }`}
                    >
                      مستلم جزئي ({supplyStats.partial})
                    </button>
                    <button
                      onClick={() => { setSupplyFilter('complete'); setSupplyPage(1); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        supplyFilter === 'complete' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                      }`}
                    >
                      مكتمل ({supplyStats.complete})
                    </button>
                  </div>
                </div>

                {/* عرض نتائج الفلتر */}
                {(supplySearch || supplyFilter !== 'all') && (
                  <div className="mb-3 text-sm text-gray-600">
                    عرض {filteredSupply.length} من {supplyStatus.length} صنف
                    {supplySearch && <span className="text-purple-600"> (بحث: &quot;{supplySearch}&quot;)</span>}
                  </div>
                )}

                {/* أزرار التصدير */}
                <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600 ml-2">تصدير:</span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExportSupply('excel', 'all')}
                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  >
                    <Download className="w-4 h-4 ml-1" />
                    الكل (Excel)
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExportSupply('excel', 'quantity')}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <Download className="w-4 h-4 ml-1" />
                    مواد الكميات (Excel)
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExportSupply('excel', 'area')}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    <Download className="w-4 h-4 ml-1" />
                    مواد المساحة (Excel)
                  </Button>
                  <span className="border-l border-gray-300 mx-2"></span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExportSupply('pdf', 'all')}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <FileText className="w-4 h-4 ml-1" />
                    الكل (PDF)
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExportSupply('pdf', 'quantity')}
                    className="text-red-500 border-red-200 hover:bg-red-50"
                  >
                    <FileText className="w-4 h-4 ml-1" />
                    مواد الكميات (PDF)
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleExportSupply('pdf', 'area')}
                    className="text-red-400 border-red-200 hover:bg-red-50"
                  >
                    <FileText className="w-4 h-4 ml-1" />
                    مواد المساحة (PDF)
                  </Button>
                </div>
                
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-3 text-right">المادة</th>
                      <th className="p-3 text-center">المطلوب</th>
                      <th className="p-3 text-center">المستلم</th>
                      <th className="p-3 text-center">المتبقي</th>
                      <th className="p-3 text-center">النسبة</th>
                      <th className="p-3 text-center w-24">إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSupply.map((item, idx) => {
                      // تحديد لون الصف حسب الحالة
                      const received = item.received_quantity || 0;
                      const required = item.required_quantity || 0;
                      let rowClass = 'border-b hover:bg-gray-100 ';
                      if (received >= required && required > 0) {
                        rowClass += 'bg-green-50'; // مستلم بالكامل
                      } else if (received > 0) {
                        rowClass += 'bg-yellow-50'; // مستلم جزئياً
                      } else {
                        rowClass += 'bg-red-50'; // لم يتم الاستلام
                      }
                      return (
                      <tr key={idx} className={rowClass}>
                        <td className="p-3">
                          <span className="text-xs font-mono text-purple-600">{item.material_code}</span>
                          <p className="font-medium">{item.material_name}</p>
                        </td>
                        <td className="p-3 text-center">{item.required_quantity?.toLocaleString()}</td>
                        <td className="p-3 text-center">
                          {editingSupply === item.material_id ? (
                            <Input
                              type="number"
                              value={receivedQty}
                              onChange={(e) => setReceivedQty(parseFloat(e.target.value) || 0)}
                              className="h-8 w-24 mx-auto"
                            />
                          ) : (
                            <span className="font-bold text-emerald-600">{item.received_quantity?.toLocaleString()}</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <span className={item.remaining_quantity > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
                            {item.remaining_quantity?.toLocaleString()}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                item.completion_percentage >= 100 ? 'bg-green-500' :
                                item.completion_percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(item.completion_percentage, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{item.completion_percentage}%</span>
                        </td>
                        <td className="p-3 text-center">
                          {editingSupply === item.material_id ? (
                            <div className="flex gap-1 justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUpdateReceived(item.material_id)}
                                className="text-green-600 h-7 w-7 p-0"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingSupply(null)}
                                className="text-gray-500 h-7 w-7 p-0"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingSupply(item.material_id); setReceivedQty(item.received_quantity || 0); }}
                              className="text-blue-600 h-7"
                            >
                              تحديث
                            </Button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {totalSupplyPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-2">
                  <div className="text-sm text-gray-500">
                    صفحة {supplyPage} من {totalSupplyPages} ({filteredSupply.length} صنف)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSupplyPage(prev => Math.max(1, prev - 1))}
                      disabled={supplyPage === 1}
                    >
                      السابق
                    </Button>
                    <div className="flex items-center gap-1">
                      {/* Show page numbers */}
                      {Array.from({ length: Math.min(5, totalSupplyPages) }, (_, i) => {
                        let pageNum;
                        if (totalSupplyPages <= 5) {
                          pageNum = i + 1;
                        } else if (supplyPage <= 3) {
                          pageNum = i + 1;
                        } else if (supplyPage >= totalSupplyPages - 2) {
                          pageNum = totalSupplyPages - 4 + i;
                        } else {
                          pageNum = supplyPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={supplyPage === pageNum ? "default" : "outline"}
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={() => setSupplyPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSupplyPage(prev => Math.min(totalSupplyPages, prev + 1))}
                      disabled={supplyPage === totalSupplyPages}
                    >
                      التالي
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Status Legend */}
              <div className="flex items-center gap-4 mt-4 text-xs text-gray-600 border-t pt-3">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-green-200 border border-green-400"></span>
                  مستلم بالكامل ({supplyStats.complete})
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-yellow-200 border border-yellow-400"></span>
                  مستلم جزئياً ({supplyStats.partial})
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-red-200 border border-red-400"></span>
                  لم يتم الاستلام ({supplyStats.notReceived})
                </span>
              </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">لم يتم تهيئة تتبع التوريد بعد</p>
                {(calculation?.materials?.length > 0 || calculation?.area_materials?.length > 0 || buildingMaterials?.length > 0) && (
                  <Button onClick={handleInitSupply} className="bg-purple-600 hover:bg-purple-700">
                    تهيئة التتبع
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Template Modal */}
      {showAddTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="bg-emerald-600 text-white p-4 rounded-t-xl">
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                إضافة نموذج شقة جديد
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">الكود *</Label>
                  <Input
                    placeholder="UNIT-C"
                    value={newTemplate.code}
                    onChange={(e) => setNewTemplate({...newTemplate, code: e.target.value.toUpperCase()})}
                    className="h-9 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs">عدد الشقق</Label>
                  <Input
                    type="number"
                    value={newTemplate.count}
                    onChange={(e) => setNewTemplate({...newTemplate, count: parseInt(e.target.value) || 0})}
                    className="h-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">اسم النموذج *</Label>
                <Input
                  placeholder="شقة نموذج ج"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">المساحة (م²)</Label>
                  <Input
                    type="number"
                    value={newTemplate.area}
                    onChange={(e) => setNewTemplate({...newTemplate, area: parseFloat(e.target.value) || 0})}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">الغرف</Label>
                  <Input
                    type="number"
                    value={newTemplate.rooms_count}
                    onChange={(e) => setNewTemplate({...newTemplate, rooms_count: parseInt(e.target.value) || 0})}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">الحمامات</Label>
                  <Input
                    type="number"
                    value={newTemplate.bathrooms_count}
                    onChange={(e) => setNewTemplate({...newTemplate, bathrooms_count: parseInt(e.target.value) || 0})}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleAddTemplate} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  إنشاء النموذج
                </Button>
                <Button onClick={() => setShowAddTemplate(false)} variant="outline" className="flex-1">
                  إلغاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
